import { toLower } from "lodash";
import moment from "moment-timezone";
import { fromUtcToStartDate, fromUtcToTimeZone, asTimeZone } from "../utils/dateFormatter";
import { create, updateField, updateFields, update, remove, getById, getAll, getBySingleQuery, getByIdQuery, getByDoubleQuery, getByQuery, getByIdDoubleQuery, updateRecord } from "../utils/dbUtils";
import { isObjectNotEmpty, isStringNotEmpty, isListNotEmpty, isNumberGreaterThanOrEqualToZero, isNotNullOrUndefined, isBooleanTrue, isStringEmpty, isNumberEmpty, isNullOrUndefined, isObjectEmpty, isNumberNotEmpty, isBooleanFalse, isStringNotEmptyOrZeroLength, isNumberGreaterThanZero } from "../utils/objectUtils";
import { randomSubscriptionNumberGenerator } from "../utils/orderUtils";
import { toE164PhoneNumber, toPhoneNumberLocal } from "../utils/stringFormatter";
import { toWritableGender } from "../utils/validationUtils";
import PaymentDetails from "./PaymentDetails";
import Address from "./Address";
import Customer from "./Customer";
import Order from "./Order";
import StatusHistory from "./StatusHistory";
import { auth, db } from "../firebase";
import { collection, doc, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where, writeBatch } from "firebase/firestore";
import EmailTemplate from "./EmailTemplate";
import UserNote from "./UserNote";
import OrderLineItem from "./OrderLineItem";
import OrderShipment from "./OrderShipment";
import OrderPayment from "./OrderPayment";
import { v4 as uuid } from 'uuid';
import Product from "./Product";
import Program from "./Program";
import { getClientIPAddress, getClientUserAgent, getReferrerUrl } from "../utils/clientUtils";
import OrderForm from "./OrderForm";
import Lead from "./Lead";
import CartItem from "./CartItem";
import OrderShippingLine from "./OrderShippingLine";
import OrderDiscountLine from "./OrderDiscountLine";
import SubscriptionForm from "./SubscriptionForm";
import EncounterForm from "./EncounterForm";
import Encounter from "./Encounter";
import Coupon from "./Coupon";
import PrescriptionRequest from "./PrescriptionRequest";
import Variant from "./Variant";
import Prescription from "./Prescription";
import ProductVariant from "./ProductVariant";
import { getProductObjById, getVariantObjById } from "../utils/entityUtils";
import Task from "./Task";
import { cancelBelugaVisit } from "../firebase/Beluga";
import { sendEmailToIT } from "../firebase/Email";

class Subscription {
    static collectionName = 'subscriptions';

    subscriptionNumber = randomSubscriptionNumberGenerator(15);
    status = "PENDING"; // enum SubscriptionStatuses
    statusHistory = []; // list of StatusHistory, historical changes to status
    userId = null; // userId of the customer
    coveredEntityId = null; // same as practiceId
    customer = new Customer(null).toDto(); // customer details snapshot
    userRefId = null; // refId of the customer in Advanced MD or EHR system
    externalRefs = {}; // mirror of user.externalRefs (kept in sync by the trackUserChanges trigger); read during shipment processing to skip redundant pharmacy patient-create calls

    discountTotal = 0.0; // total discounts to be subtracted from order to calculate subtotal
    discountTax = 0.0; // tax to be subtracted from tax total

    shippingTotal = 0.0; // total for shipping method (w/o tax)
    shippingTax = 0.0; // tax for shipping method

    feeTotal = 0.0; // total for all fee line items (w/o tax)
    feeTax = 0.0; // tax for all fee line items

    cartTotal = 0.0; // total of all order line items (w/o tax)
    cartTax = 0.0; // tax for all order line items

    subtotal = 0.0; // subtotal for the order
    tax = 0.0; // total tax for the order
    total = 0.0; // overall order total

    lineItems = [];
    taxLines = [];
    shippingLines = [];
    feeLines = [];
    discountLines = [];
    notes = [];
    metaData = []; // array of order meta data

    parentOrderId = null; // a subscription can only have one parent order (the order that initiated the subscription with optional sign-up fee and first order), but a subscription can also have no parent order if the subscription is future dated and no order is placed at the time the subscription is setup
    relatedOrderIds = []; // includes parent order, renewal orders

    billingAddress = new Address(null).toDto();
    shippingAddress = new Address(null).toDto();

    paymentMethods = []; // array of PaymentDetails

    customerIPAddress = null;
    customerUserAgent = null;

    customerNote = null; // note provided by the customer at the time they placed the subscription

    startDate = moment().tz('US/Pacific').startOf('day').utc().toDate(); // Subscription start date
    nextPaymentDate = null; // Subscription next payment date
    lastPaymentDate = null; // Subscription's most recent parent or renewal order created date
    endDate = null; // Subscription end date (defaults to null when the subscription has no end)
    cancelledDate = null;
    failedOrderAt = null; // orderedAt date of the first order with a failed payment that has not yet been resolved
    suspendedAt = null; // date when subscription transitions to SUSPENDED status

    billingPeriod = "MONTH";
    billingInterval = 1;
    billingCycles = 0;

    cancelledEmailSent = false;
    welcomeEmailSent = false;
    welcomeCallStatus = 'PENDING'; // enum CallStatuses
    welcomeCallDueAt = null;
    welcomeCallId = null;
    bypassBillingStatus = false;

    source = null;
    affiliateId = null;
    subAffiliateId = null;
    campaignId = null;
    programId = null;
    productSku = null;
    productFamilyId = null;
    offerGroupId = null;
    offerToken = null;
    commercialEquivalencyGroupId = null;
    productId = null;
    variantId = null;

    conversionId = null;
    isConversion = false;

    affiliateInfo = null;
    deviceInfo = null;
    emailInfo = null;
    phoneInfo = null;

    sentEmails = [];

    isStacked = false;
    stackedOptions = []; // { productCategoryId, (productId, variantId), isRequired }
    stackedSelections = []; // filled out based on what the clinician actually prescribed { productCategoryId, (productId, variantId) }
    stackEncounters = false;
    stackShipments = false;

    isCustomerRescheduled = false; // true while the next fill date is the one the customer chose; a fill clears it
    rescheduleHistory = []; // array of RescheduleHistoryEntry

    forms = []; // array of SubscriptionForm
    formsCount = 0;

    requiresConfirmation = false; // requires patient confirmation - needs to be set to true if there are surveys and they need to be filled out for each order
    requiresClinicianApproval = false; // requires that the doctor prescribes/approves the product
    requiresClinicianEncounter = false; // requires an in-person or telehealth dr appt
    requiresSurveys = false; // requires patient to fill out forms/surveys

    // require documents to be provided when ordering product
    requiredDocuments = []; // array of object { documentType, requirement, scope } requirement: enum DocumentRequirements, scope: enum SurveyScopes

    leadId = null;

    pharmacyId = null; // pharmacy to use if this subscription is for prescription product

    bundleShipmentCount = 1;
    ordersPerEncounter = 1; // how many orders can be approved at the same time in one encounter
    bundlePaymentCount = 1; // how many orders should be paid for together in a bundled up-front payment
    bundlePaymentCouponCode = null;

    encounterType = 'NONE';
    encounterFrequency = 1;
    encounterPrice = 0.0;

    nextShipmentDate = null;
    lastShipmentDate = null;

    nextEncounterDate = null;
    lastEncounterDate = null;

    nextVariantLevel = 1;
    lastVariantLevel = 1;

    repId = 'NONE'; // userId of customer representative

    includesUpcharge = false;
    upcharge = 0;

    testMode = false;

    // Metadata for cancellation and pause details
    metadata = {}; // stores cancellation details, pause details, etc.

    // Test schedule (snapshot-copied from Product at subscription creation)
    testSchedule = []; // TestScheduleEntry[]

    // Annual cycle tracking
    cycleStartDate = null;
    currentCycleYear = 1;
    baselineCompleted = false;

    // Next/last test tracking
    nextTestDate = null;
    lastTestDate = null;
    nextScheduleEntryId = null;

    // Completed visits in current cycle
    completedVisits = []; // CompletedVisit[]

    // Active treatment tracking (for computing quarterly treatment panels)
    activeTreatmentProductIds = [];

    constructor(subscription) {
        if (isObjectNotEmpty(subscription)) {
            if (isStringNotEmpty(subscription.subscriptionNumber)) { this.subscriptionNumber = subscription.subscriptionNumber }

            if (isStringNotEmpty(subscription.status)) { this.status = subscription.status; }
            if (isListNotEmpty(subscription.statusHistory)) { this.statusHistory = subscription.statusHistory; }
            else if (isStringNotEmpty(this.status)) { this.statusHistory = StatusHistory.addNewStatus(this.status, []); }

            if (isStringNotEmpty(subscription.userId)) { this.userId = subscription.userId; }
            if (isStringNotEmpty(subscription.coveredEntityId)) { this.coveredEntityId = subscription.coveredEntityId; }
            if (isObjectNotEmpty(subscription.customer)) { this.customer = subscription.customer; }
            if (isStringNotEmpty(subscription.userRefId)) { this.userRefId = subscription.userRefId; }
            if (isObjectNotEmpty(subscription.externalRefs)) { this.externalRefs = subscription.externalRefs; }

            if (isNumberGreaterThanOrEqualToZero(subscription.discountTotal)) { this.discountTotal = subscription.discountTotal; }
            if (isNumberGreaterThanOrEqualToZero(subscription.discountTax)) { this.discountTax = subscription.discountTax; }
            if (isNumberGreaterThanOrEqualToZero(subscription.shippingTotal)) { this.shippingTotal = subscription.shippingTotal; }
            if (isNumberGreaterThanOrEqualToZero(subscription.shippingTax)) { this.shippingTax = subscription.shippingTax; }
            if (isNumberGreaterThanOrEqualToZero(subscription.feeTotal)) { this.feeTotal = subscription.feeTotal; }
            if (isNumberGreaterThanOrEqualToZero(subscription.feeTax)) { this.feeTax = subscription.feeTax; }
            if (isNumberGreaterThanOrEqualToZero(subscription.cartTotal)) { this.cartTotal = subscription.cartTotal; }
            if (isNumberGreaterThanOrEqualToZero(subscription.cartTax)) { this.cartTax = subscription.cartTax; }
            if (isNumberGreaterThanOrEqualToZero(subscription.subtotal)) { this.subtotal = subscription.subtotal; }
            if (isNumberGreaterThanOrEqualToZero(subscription.tax)) { this.tax = subscription.tax; }
            if (isNumberGreaterThanOrEqualToZero(subscription.total)) { this.total = subscription.total; }

            if (isListNotEmpty(subscription.lineItems)) { this.lineItems = subscription.lineItems; }
            if (isListNotEmpty(subscription.taxLines)) { this.taxLines = subscription.taxLines; }
            if (isListNotEmpty(subscription.shippingLines)) { this.shippingLines = subscription.shippingLines; }
            if (isListNotEmpty(subscription.feeLines)) { this.feeLines = subscription.feeLines; }
            if (isListNotEmpty(subscription.discountLines)) { this.discountLines = subscription.discountLines; }
            if (isListNotEmpty(subscription.notes)) { this.notes = subscription.notes; }
            if (isListNotEmpty(subscription.metaData)) { this.metaData = subscription.metaData; }

            if (isStringNotEmpty(subscription.parentOrderId)) { this.parentOrderId = subscription.parentOrderId; }
            if (isListNotEmpty(subscription.relatedOrderIds)) { this.relatedOrderIds = subscription.relatedOrderIds; }

            if (isObjectNotEmpty(subscription.billingAddress)) { this.billingAddress = subscription.billingAddress; }
            if (isObjectNotEmpty(subscription.shippingAddress)) { this.shippingAddress = subscription.shippingAddress; }

            if (isListNotEmpty(subscription.paymentMethods)) { this.paymentMethods = subscription.paymentMethods; }

            if (isStringNotEmpty(subscription.customerIPAddress)) { this.customerIPAddress = subscription.customerIPAddress; }
            if (isStringNotEmpty(subscription.customerUserAgent)) { this.customerUserAgent = subscription.customerUserAgent; }

            if (isStringNotEmpty(subscription.customerNote)) { this.customerNote = subscription.customerNote; }

            if (isNotNullOrUndefined(subscription.startDate)) { this.startDate = subscription.startDate; }
            if (isNotNullOrUndefined(subscription.nextPaymentDate)) { this.nextPaymentDate = subscription.nextPaymentDate; }
            if (isNotNullOrUndefined(subscription.lastPaymentDate)) { this.lastPaymentDate = subscription.lastPaymentDate; }
            if (isNotNullOrUndefined(subscription.endDate)) { this.endDate = subscription.endDate; }
            if (isNotNullOrUndefined(subscription.cancelledDate)) { this.cancelledDate = subscription.cancelledDate; }
            if (isNotNullOrUndefined(subscription.failedOrderAt)) { this.failedOrderAt = subscription.failedOrderAt; }
            if (isNotNullOrUndefined(subscription.suspendedAt)) { this.suspendedAt = subscription.suspendedAt; }

            if (isStringNotEmpty(subscription.billingPeriod)) { this.billingPeriod = subscription.billingPeriod; }
            if (isNumberGreaterThanOrEqualToZero(subscription.billingInterval)) { this.billingInterval = subscription.billingInterval; }
            if (isNumberGreaterThanOrEqualToZero(subscription.billingCycles)) { this.billingCycles = subscription.billingCycles; }

            if (isBooleanTrue(subscription.cancelledEmailSent)) { this.cancelledEmailSent = true; }
            if (isBooleanTrue(subscription.welcomeEmailSent)) { this.welcomeEmailSent = true; }
            if (isStringNotEmpty(subscription.welcomeCallStatus)) { this.welcomeCallStatus = subscription.welcomeCallStatus; }
            if (isNotNullOrUndefined(subscription.welcomeCallDueAt)) { this.welcomeCallDueAt = subscription.welcomeCallDueAt; }
            if (isStringNotEmpty(subscription.welcomeCallId)) { this.welcomeCallId = subscription.welcomeCallId; }
            if (isBooleanTrue(subscription.bypassBillingStatus)) { this.bypassBillingStatus = true; }

            if (isStringNotEmpty(subscription.source)) { this.source = subscription.source; }
            if (isStringNotEmpty(subscription.affiliateId)) { this.affiliateId = toLower(subscription.affiliateId); }
            if (isStringNotEmpty(subscription.subAffiliateId)) { this.subAffiliateId = subscription.subAffiliateId; }
            if (isStringNotEmpty(subscription.campaignId)) { this.campaignId = subscription.campaignId; }
            if (isStringNotEmpty(subscription.programId)) { this.programId = subscription.programId; }
            if (isStringNotEmpty(subscription.productSku)) { this.productSku = subscription.productSku; }
            if (isStringNotEmpty(subscription.productFamilyId)) { this.productFamilyId = subscription.productFamilyId; }
            if (isStringNotEmpty(subscription.offerGroupId)) { this.offerGroupId = subscription.offerGroupId; }
            if (isStringNotEmpty(subscription.offerToken)) { this.offerToken = subscription.offerToken; }
            if (isStringNotEmpty(subscription.commercialEquivalencyGroupId)) { this.commercialEquivalencyGroupId = subscription.commercialEquivalencyGroupId; }
            if (isStringNotEmpty(subscription.productId)) { this.productId = subscription.productId; }
            if (isStringNotEmpty(subscription.variantId)) { this.variantId = subscription.variantId; }

            if (isListNotEmpty(subscription.sentEmails)) { this.sentEmails = subscription.sentEmails; }

            if (isBooleanTrue(subscription.isStacked)) { this.isStacked = true; }
            if (isListNotEmpty(subscription.stackedOptions)) { this.stackedOptions = subscription.stackedOptions; }
            if (isListNotEmpty(subscription.stackedSelections)) { this.stackedSelections = subscription.stackedSelections; }
            if (isBooleanTrue(subscription.stackEncounters)) { this.stackEncounters = true; }
            if (isBooleanTrue(subscription.stackShipments)) { this.stackShipments = true; }

            if (isBooleanTrue(subscription.isCustomerRescheduled)) { this.isCustomerRescheduled = true; }
            if (isListNotEmpty(subscription.rescheduleHistory)) { this.rescheduleHistory = subscription.rescheduleHistory; }

            if (isListNotEmpty(subscription.forms)) {
                this.forms = subscription.forms;
                this.formsCount = subscription.forms.length;
            }

            if (isBooleanTrue(subscription.requiresConfirmation)) { this.requiresConfirmation = true; }
            if (isBooleanTrue(subscription.requiresClinicianApproval)) { this.requiresClinicianApproval = true; }
            if (isBooleanTrue(subscription.requiresClinicianEncounter)) { this.requiresClinicianEncounter = true; }
            if (isBooleanTrue(subscription.requiresSurveys)) { this.requiresSurveys = true; }

            if (isListNotEmpty(subscription.requiredDocuments)) { this.requiredDocuments = subscription.requiredDocuments; }

            if (isStringNotEmpty(subscription.conversionId)) { this.conversionId = subscription.conversionId; }
            if (isBooleanTrue(subscription.isConversion)) { this.isConversion = true; }

            if (isObjectNotEmpty(subscription.affiliateInfo)) { this.affiliateInfo = subscription.affiliateInfo; }
            if (isObjectNotEmpty(subscription.deviceInfo)) { this.deviceInfo = subscription.deviceInfo; }
            if (isObjectNotEmpty(subscription.emailInfo)) { this.emailInfo = subscription.emailInfo; }
            if (isObjectNotEmpty(subscription.phoneInfo)) { this.phoneInfo = subscription.phoneInfo; }

            if (isStringNotEmpty(subscription.leadId)) { this.leadId = subscription.leadId; }

            if (isStringNotEmpty(subscription.pharmacyId)) { this.pharmacyId = subscription.pharmacyId; }

            if (isNumberGreaterThanOrEqualToZero(subscription.bundleShipmentCount)) { this.bundleShipmentCount = subscription.bundleShipmentCount; }
            if (isNumberGreaterThanOrEqualToZero(subscription.ordersPerEncounter)) { this.ordersPerEncounter = subscription.ordersPerEncounter; }
            if (isNumberGreaterThanOrEqualToZero(subscription.bundlePaymentCount)) { this.bundlePaymentCount = subscription.bundlePaymentCount; }
            if (isStringNotEmpty(subscription.bundlePaymentCouponCode)) { this.bundlePaymentCouponCode = subscription.bundlePaymentCouponCode; }

            if (isStringNotEmpty(subscription.encounterType)) { this.encounterType = subscription.encounterType; }
            if (isNumberGreaterThanOrEqualToZero(subscription.encounterFrequency)) { this.encounterFrequency = subscription.encounterFrequency; }
            if (isNumberGreaterThanOrEqualToZero(subscription.encounterPrice)) { this.encounterPrice = subscription.encounterPrice; }

            if (isNotNullOrUndefined(subscription.nextShipmentDate)) { this.nextShipmentDate = subscription.nextShipmentDate; }
            if (isNotNullOrUndefined(subscription.lastShipmentDate)) { this.lastShipmentDate = subscription.lastShipmentDate; }

            if (isNotNullOrUndefined(subscription.nextEncounterDate)) { this.nextEncounterDate = subscription.nextEncounterDate; }
            if (isNotNullOrUndefined(subscription.lastEncounterDate)) { this.lastEncounterDate = subscription.lastEncounterDate; }

            if (isNumberGreaterThanOrEqualToZero(subscription.nextVariantLevel)) { this.nextVariantLevel = subscription.nextVariantLevel; }
            if (isNumberGreaterThanOrEqualToZero(subscription.lastVariantLevel)) { this.lastVariantLevel = subscription.lastVariantLevel; }

            if (isStringNotEmpty(subscription.repId)) { this.repId = subscription.repId; }

            if (isBooleanTrue(subscription.includesUpcharge)) { this.includesUpcharge = true; }
            if (isNumberGreaterThanOrEqualToZero(subscription.upcharge)) { this.upcharge = subscription.upcharge; }

            if (isBooleanTrue(subscription.testMode)) { this.testMode = true; }

            // Metadata
            if (isObjectNotEmpty(subscription.metadata)) { this.metadata = subscription.metadata; }

            // Test schedule
            if (isListNotEmpty(subscription.testSchedule)) { this.testSchedule = subscription.testSchedule; }

            // Annual cycle tracking
            if (isNotNullOrUndefined(subscription.cycleStartDate)) { this.cycleStartDate = subscription.cycleStartDate; }
            if (isNumberGreaterThanOrEqualToZero(subscription.currentCycleYear)) { this.currentCycleYear = subscription.currentCycleYear; }
            if (isBooleanTrue(subscription.baselineCompleted)) { this.baselineCompleted = true; }

            // Next/last test tracking
            if (isNotNullOrUndefined(subscription.nextTestDate)) { this.nextTestDate = subscription.nextTestDate; }
            if (isNotNullOrUndefined(subscription.lastTestDate)) { this.lastTestDate = subscription.lastTestDate; }
            if (isStringNotEmpty(subscription.nextScheduleEntryId)) { this.nextScheduleEntryId = subscription.nextScheduleEntryId; }

            // Completed visits
            if (isListNotEmpty(subscription.completedVisits)) { this.completedVisits = subscription.completedVisits; }

            // Active treatment tracking
            if (isListNotEmpty(subscription.activeTreatmentProductIds)) { this.activeTreatmentProductIds = subscription.activeTreatmentProductIds; }
        }
    }

    toDto = () => {
        return {
            subscriptionNumber: this.subscriptionNumber,
            status: this.status,
            statusHistory: this.statusHistory,

            userId: this.userId,
            coveredEntityId: this.coveredEntityId,
            customer: this.customer,
            userRefId: this.userRefId,
            externalRefs: this.externalRefs,

            discountTotal: this.discountTotal,
            discountTax: this.discountTax,

            shippingTotal: this.shippingTotal,
            shippingTax: this.shippingTax,

            feeTotal: this.feeTotal,
            feeTax: this.feeTax,

            cartTotal: this.cartTotal,
            cartTax: this.cartTax,

            subtotal: this.subtotal,
            tax: this.tax,
            total: this.total,

            lineItems: this.lineItems,
            taxLines: this.taxLines,
            shippingLines: this.shippingLines,
            feeLines: this.feeLines,
            discountLines: this.discountLines,
            notes: this.notes,
            metaData: this.metaData,

            parentOrderId: this.parentOrderId,
            relatedOrderIds: this.relatedOrderIds,

            billingAddress: this.billingAddress,
            shippingAddress: this.shippingAddress,

            paymentMethods: this.paymentMethods,

            customerIPAddress: this.customerIPAddress,
            customerUserAgent: this.customerUserAgent,

            customerNote: this.customerNote,

            startDate: this.startDate,
            nextPaymentDate: this.nextPaymentDate,
            lastPaymentDate: this.lastPaymentDate,
            endDate: this.endDate,
            cancelledDate: this.cancelledDate,
            failedOrderAt: this.failedOrderAt,
            suspendedAt: this.suspendedAt,

            billingPeriod: this.billingPeriod,
            billingInterval: this.billingInterval,
            billingCycles: this.billingCycles,

            cancelledEmailSent: this.cancelledEmailSent,
            welcomeEmailSent: this.welcomeEmailSent,
            welcomeCallStatus: this.welcomeCallStatus,
            welcomeCallDueAt: this.welcomeCallDueAt,
            welcomeCallId: this.welcomeCallId,
            bypassBillingStatus: this.bypassBillingStatus,

            source: this.source,
            affiliateId: this.affiliateId,
            subAffiliateId: this.subAffiliateId,
            campaignId: this.campaignId,
            programId: this.programId,
            productSku: this.productSku,
            productFamilyId: this.productFamilyId,
            offerGroupId: this.offerGroupId,
            offerToken: this.offerToken,
            commercialEquivalencyGroupId: this.commercialEquivalencyGroupId,
            productId: this.productId,
            variantId: this.variantId,

            isStacked: this.isStacked,
            stackedOptions: this.stackedOptions,
            stackedSelections: this.stackedSelections,
            stackEncounters: this.stackEncounters,
            stackShipments: this.stackShipments,

            isCustomerRescheduled: this.isCustomerRescheduled,
            rescheduleHistory: this.rescheduleHistory,

            sentEmails: this.sentEmails,

            forms: this.forms,
            formsCount: this.formsCount,

            requiresConfirmation: this.requiresConfirmation,
            requiresClinicianApproval: this.requiresClinicianApproval,
            requiresClinicianEncounter: this.requiresClinicianEncounter,
            requiresSurveys: this.requiresSurveys,

            requiredDocuments: this.requiredDocuments,

            conversionId: this.conversionId,
            isConversion: this.isConversion,

            affiliateInfo: this.affiliateInfo,
            deviceInfo: this.deviceInfo,
            emailInfo: this.emailInfo,
            phoneInfo: this.phoneInfo,

            leadId: this.leadId,

            pharmacyId: this.pharmacyId,

            bundleShipmentCount: this.bundleShipmentCount,
            ordersPerEncounter: this.ordersPerEncounter,
            bundlePaymentCount: this.bundlePaymentCount,
            bundlePaymentCouponCode: this.bundlePaymentCouponCode,

            encounterType: this.encounterType,
            encounterFrequency: this.encounterFrequency,
            encounterPrice: this.encounterPrice,

            nextShipmentDate: this.nextShipmentDate,
            lastShipmentDate: this.lastShipmentDate,

            nextEncounterDate: this.nextEncounterDate,
            lastEncounterDate: this.lastEncounterDate,

            nextVariantLevel: this.nextVariantLevel,
            lastVariantLevel: this.lastVariantLevel,

            repId: this.repId,

            includesUpcharge: this.includesUpcharge,
            upcharge: this.upcharge,

            testMode: this.testMode,

            // Metadata
            metadata: this.metadata,

            // Test schedule
            testSchedule: this.testSchedule,

            // Annual cycle tracking
            cycleStartDate: this.cycleStartDate,
            currentCycleYear: this.currentCycleYear,
            baselineCompleted: this.baselineCompleted,

            // Next/last test tracking
            nextTestDate: this.nextTestDate,
            lastTestDate: this.lastTestDate,
            nextScheduleEntryId: this.nextScheduleEntryId,

            // Completed visits
            completedVisits: this.completedVisits,

            // Active treatment tracking
            activeTreatmentProductIds: this.activeTreatmentProductIds
        }
    }

    isValid = () => {
        if (isStringEmpty(this.userId)) {
            throw new Error('userId is required.');
        }
        if (isNumberEmpty(this.subtotal)) {
            throw new Error('subtotal is required.');
        }
        if (isNumberEmpty(this.tax)) {
            throw new Error('tax is required.');
        }
        if (isNumberEmpty(this.total)) {
            throw new Error('total is required.');
        }
        if (isNullOrUndefined(this.startDate)) {
            throw new Error('startDate is required.');
        }
        if (isStringEmpty(this.billingPeriod)) {
            throw new Error('billingPeriod is required.');
        }
        if (isNumberEmpty(this.billingInterval)) {
            throw new Error('billingInterval is required.');
        }
        if (isNumberEmpty(this.billingCycles)) {
            throw new Error('billingCycles is required.');
        }

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            return await create(Subscription.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Subscription.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id)) {
            return await updateFields(Subscription.collectionName, id, updatedFields);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedSubscription) => {
        const updatedObj = new Subscription(updatedSubscription);
        if (updatedObj.isValid() && id === updatedSubscription.id) {
            return await update(Subscription.collectionName, id, updatedObj.toDto());
        } else {
            throw new Error('id and id field of the updated subscription must match.')
        }
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(Subscription.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(Subscription.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(Subscription.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Subscription.collectionName, fieldName, fieldValue, comparer);
    }

    static getByUserId = async (userId) => {
        return await getBySingleQuery(Subscription.collectionName, 'userId', userId, '==');
    }

    static getByUserIdAndSubscriptionId = async (userId, subscriptionId) => {
        let userSubscriptions = await this.getByUserId(userId);
        if (isListNotEmpty(userSubscriptions)) {
            return userSubscriptions.find(sub => sub.id === subscriptionId) ?? null;
        }

        return null;
    }

    static getByCoveredEntityId = async (coveredEntityId) => {
        return await getBySingleQuery(Subscription.collectionName, 'coveredEntityId', coveredEntityId, '==');
    }

    static getByCoveredEntityIdAndUserId = async (coveredEntityId, userId) => {
        return await getByDoubleQuery(Subscription.collectionName, 'coveredEntityId', coveredEntityId, '==', 'userId', userId, '==');
    }

    static getByUserIdAndProgramId = async (userId, programId) => {
        return await getByDoubleQuery(Subscription.collectionName, 'userId', userId, '==', 'programId', programId, '==');
    }

    static getByUserIdAndStatus = async (userId, status) => {
        let queryConstraints = [];

        queryConstraints.push(where('userId', '==', userId));

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }


        let q = query(collection(db, Subscription.collectionName), ...queryConstraints);

        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static getByUserIdAndProgramIdAndStatus = async (userId, programId, status) => {
        let queryConstraints = [];

        queryConstraints.push(where('userId', '==', userId));

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }


        let q = query(collection(db, Subscription.collectionName), ...queryConstraints);

        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Subscription.collectionName, ids, 'in');
    }

    static getByIdsAndUserId = async (ids = [], userId) => {
        return await getByIdDoubleQuery(Subscription.collectionName, ids, 'in', 'userId', userId, '==');
    }

    static getByQueryConstraints = async (queryConstraints = []) => {
        let q = query(collection(db, Subscription.collectionName), ...queryConstraints);

        const snapShot = await getDocs(q);
        // console.log(snapShot)
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id, // append document id to each document
                ...doc.data()
            };
        });
    }

    static getByCoveredEntityIdAndPhone = async (coveredEntityId, phone) => {
        let queryConstraints = [];

        if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }
        if (isStringNotEmptyOrZeroLength(phone)) {
            const cleanPhone = toPhoneNumberLocal(phone);
            const formattedPhone = toE164PhoneNumber(phone);
            queryConstraints.push(where('customer.phone', 'in', [phone, formattedPhone, cleanPhone]));
        }

        return await this.getByQueryConstraints(queryConstraints);
    }

    static getByCoveredEntityIdAndEmail = async (coveredEntityId, email) => {
        let queryConstraints = [];

        if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }
        if (isStringNotEmptyOrZeroLength(email)) {
            queryConstraints.push(where('customer.email', '==', toLower(email)));
        }

        return await this.getByQueryConstraints(queryConstraints);
    }

    static getByCoveredEntityIdAndFirstNameAndLastNameAndEmailAndPhoneAndPatientId = async (coveredEntityId, firstName, lastName, email, phone, patientId) => {
        let queryConstraints = [];

        if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }
        if (isStringNotEmptyOrZeroLength(firstName)) {
            queryConstraints.push(where('customer.firstNameLower', '==', toLower(firstName.trim())));
        }
        if (isStringNotEmptyOrZeroLength(lastName)) {
            queryConstraints.push(where('customer.lastNameLower', '==', toLower(lastName.trim())));
        }
        if (isStringNotEmptyOrZeroLength(email)) {
            queryConstraints.push(where('customer.email', '==', toLower(email.trim())));
        }
        if (isStringNotEmptyOrZeroLength(phone)) {
            const cleanPhone = toPhoneNumberLocal(phone.trim());
            const formattedPhone = toE164PhoneNumber(phone.trim());
            queryConstraints.push(where('customer.phone', 'in', [phone.trim(), formattedPhone, cleanPhone]));
        }
        if (isStringNotEmptyOrZeroLength(patientId)) {
            queryConstraints.push(where('userRefId', '==', patientId.trim()));
        }

        queryConstraints.push(orderBy('createdAt', 'asc'));
        queryConstraints.push(orderBy('__name__', 'asc'));

        return await this.getByQueryConstraints(queryConstraints);
    }

    static startPrescriptionSubscription = async (userId, profile, product, products, variant, variants, billingFrequency, startingLevel = 1, forms, cartItems, coupon, taxRate, shippingMethod, shippingAddress, billingAddress, ipAddress, deviceInfo, emailInfo, phoneInfo, program, existingSubscription = null, existingFirstOrder = null, existingEncounter = null, existingLead, titrationDirection = 'INCREASE', isConfirmed = false, isSurveysCompleted = false, isRequiredDocumentsProvided = false, testMode = false) => {
        // console.log('startPrescriptionSubscription startingLevel', startingLevel);
        // console.log('startPrescriptionSubscription titrationDirection', titrationDirection);

        // this is the order that the auth transaction will be associated with and is passed back from this method with the subscription
        let firstOrder = isObjectNotEmpty(existingFirstOrder) ? { ...existingFirstOrder } : null;
        if (isStringNotEmpty(userId) && isObjectNotEmpty(profile) && isObjectNotEmpty(product) && isObjectNotEmpty(program) && isListNotEmpty(forms)) {
            let customerUserAgent = getClientUserAgent();
            let customerReferrerUrl = getReferrerUrl();

            let productObj = new Product(product);

            let availableVariants = variants?.filter(i => i.productId === productObj?.id && isBooleanTrue(i.enabled));
            // console.log('variants', availableVariants);
            // console.log('product', productObj);
            let nextLevel = startingLevel;
            let maxLevel = availableVariants.length;
            let startingVariant = availableVariants.find(i => i.titrationLevel === startingLevel);
            let startingVariantObj = new Variant(startingVariant);
            let hasUpcharge = false;
            let upcharge = 0;
            if (isNotNullOrUndefined(startingVariantObj) && isNumberNotEmpty(startingVariantObj?.upcharge)) {
                if (startingVariantObj?.upcharge > 0) {
                    hasUpcharge = true;
                    upcharge = startingVariantObj?.upcharge;
                }
            }

            try {
                let subscriptionLineItems = [];
                if (isNotNullOrUndefined(productObj) && isNotNullOrUndefined(startingVariantObj)) {
                    let quantity = 1;
                    let unitPrice = startingVariantObj.getPrice();
                    let subtotal = quantity * unitPrice;
                    let tax = Order.getLineItemTaxTotal(product, quantity, taxRate);
                    let total = subtotal + tax;
                    subscriptionLineItems.push(new OrderLineItem({
                        name: productObj.name,
                        sku: productObj.sku,
                        productId: productObj.id,
                        variantId: variant?.id ?? null, // keep null at subscription level if product is a titrating product where the variant changes over time
                        quantity: quantity,
                        price: unitPrice,
                        subtotal: subtotal,
                        tax: tax,
                        total: total
                    }).toDto());
                }

                let subscriptionDiscountLines = [];
                if (isObjectNotEmpty(coupon) && isStringNotEmpty(coupon.code)) {
                    let discountRes = CartItem.getCartDiscount(coupon, cartItems, taxRate, products, variants);
                    // console.log('discountRes', discountRes);
                    let discountTotal = discountRes?.discountTotal ?? 0;
                    let discountTax = discountRes?.discountTax ?? 0;
                    // the subscription stores one month; each order multiplies it by bundlePaymentCount
                    subscriptionDiscountLines.push(new OrderDiscountLine({
                        code: coupon?.code,
                        discount: discountTotal,
                        discountTax: discountTax,
                        discountTotal: discountTotal + discountTax,
                        recurring: isBooleanTrue(['RECURRING', 'BUNDLE', 'MIXED_RECURRING'].includes(coupon?.discountTarget))
                    }).toDto());

                    if (isNotNullOrUndefined(startingVariantObj) && isNumberNotEmpty(startingVariantObj?.upcharge)) {
                        if (startingVariantObj?.upcharge > 0 && (coupon.discountTarget === 'RECURRING' || coupon.discountTarget === 'MIXED_RECURRING')) {
                            upcharge = upcharge - (discountTotal * (upcharge / startingVariantObj.getPrice()));
                        }
                    }
                }

                let bundlePaymentCouponCode = null;
                if (isNumberGreaterThanOrEqualToZero(billingFrequency) && billingFrequency > 1) {
                    let productBundleDiscounts = isListNotEmpty(productObj?.bundleDiscounts) ? [...productObj.bundleDiscounts] : [];
                    let bundleDiscount = productBundleDiscounts.find(i => i.bundlePaymentCount === billingFrequency);
                    if (isObjectNotEmpty(bundleDiscount)) {
                        let bundleCoupon = await Coupon.getByCode(bundleDiscount.couponCode);
                        if (isObjectNotEmpty(bundleCoupon)) {
                            bundlePaymentCouponCode = bundleDiscount.couponCode;
                            // let bundleDiscountRes = Order.getOrderDiscount(bundleCoupon, [...lineItems], taxRate, billingFrequency);
                            // let bundleDiscountTotal = bundleDiscountRes?.discountTotal ?? 0;
                            // let bundleDiscountTax = bundleDiscountRes?.discountTax ?? 0;
                            // discountLines.push(new OrderDiscountLine({
                            //     code: bundleCoupon?.code,
                            //     discount: bundleDiscountTotal,
                            //     discountTax: bundleDiscountTax,
                            //     discountTotal: bundleDiscountTotal + bundleDiscountTax,
                            //     recurring: true
                            // }).toDto());
                        }
                    }
                }

                let subscriptionShippingLines = [];
                if (isObjectNotEmpty(shippingMethod)) {
                    let shippingMethodCostRes = CartItem.calculateShippingMethodCost(shippingMethod, isListNotEmpty(cartItems) ? [...cartItems] : [], taxRate, products, variants);
                    subscriptionShippingLines.push(new OrderShippingLine({
                        methodTitle: shippingMethod.name,
                        methodId: shippingMethod.id,
                        taxStatus: shippingMethod.taxStatus,
                        subtotal: shippingMethodCostRes.shippingTotal,
                        tax: shippingMethodCostRes.shippingTax,
                        total: shippingMethodCostRes.shippingTotal + shippingMethodCostRes.shippingTax
                    }).toDto());
                }

                let subscriptionOrderSummary = Order.calculateOrderSummary(subscriptionLineItems, subscriptionDiscountLines, [], subscriptionShippingLines);
                // console.log('subscriptionOrderSummary', subscriptionOrderSummary);
                let stateProductRule = productObj.productRules.find(i => i.state === shippingAddress?.state);

                // save forms to subscription
                let subscriptionForms = isListNotEmpty(forms) ? forms.map((form) => {
                    return new SubscriptionForm({ ...form }).toDto();
                }) : [];

                let newSubscription = isObjectNotEmpty(existingSubscription) ? { ...existingSubscription } : null;
                if (isObjectEmpty(newSubscription)) {
                    // create the parent order that will trigger the subscription to be created
                    let newSubscriptionFields = {
                        status: 'PENDING',

                        userId: userId,
                        customer: new Customer({ ...profile }).toDto(),

                        // default to the state product rule first
                        coveredEntityId: stateProductRule?.practiceId ?? (productObj?.practiceId || null),

                        discountTotal: subscriptionOrderSummary?.discountTotal ?? 0,
                        discountTax: subscriptionOrderSummary?.discountTax ?? 0,

                        shippingTotal: subscriptionOrderSummary?.shippingTotal ?? 0,
                        shippingTax: subscriptionOrderSummary?.shippingTax ?? 0,

                        feeTotal: subscriptionOrderSummary?.feeTotal ?? 0,
                        feeTax: subscriptionOrderSummary?.feeTax ?? 0,

                        cartTotal: subscriptionOrderSummary?.cartTotal ?? 0,
                        cartTax: subscriptionOrderSummary?.cartTax ?? 0,

                        subtotal: subscriptionOrderSummary?.subtotal ?? 0,
                        tax: subscriptionOrderSummary?.tax ?? 0,
                        total: subscriptionOrderSummary?.total ?? 0,

                        lineItems: subscriptionLineItems,
                        discountLines: subscriptionDiscountLines,
                        shippingLines: subscriptionShippingLines,

                        shippingAddress: shippingAddress,
                        billingAddress: billingAddress,

                        customerIPAddress: ipAddress,
                        customerUserAgent: customerUserAgent,

                        customerNote: null,

                        billingPeriod: productObj?.subscriptionPeriod ?? "MONTH",
                        billingInterval: productObj?.subscriptionPeriodInterval ?? 1,
                        billingCycles: productObj?.subscriptionLength ?? 0,

                        source: existingLead?.source,
                        affiliateId: existingLead?.affiliateId,
                        subAffiliateId: existingLead?.subAffiliateId,
                        campaignId: existingLead?.campaignId,
                        programId: existingLead?.programId,
                        productSku: productObj?.sku ?? null,
                        productFamilyId: existingLead?.productFamilyId ?? productObj?.productFamilyId ?? null,
                        offerGroupId: existingLead?.offerGroupId ?? productObj?.offerGroupId ?? null,
                        offerToken: existingLead?.offerToken ?? null,
                        commercialEquivalencyGroupId: existingLead?.commercialEquivalencyGroupId ?? productObj?.commercialEquivalencyGroupId ?? null,
                        productId: productObj?.id ?? null,
                        variantId: variant?.id ?? null,

                        affiliateInfo: existingLead?.affiliateInfo,
                        deviceInfo: deviceInfo,
                        emailInfo: emailInfo,
                        phoneInfo: phoneInfo,

                        // default to the state product rule first
                        requiresConfirmation: stateProductRule?.requiresConfirmation ?? productObj?.requiresConfirmation,
                        requiresClinicianApproval: stateProductRule?.requiresClinicianApproval ?? productObj?.requiresClinicianApproval,
                        requiresClinicianEncounter: stateProductRule?.requiresClinicianEncounter ?? productObj?.requiresClinicianEncounter,
                        requiresSurveys: stateProductRule?.requiresSurveys ?? productObj?.requiresSurveys,

                        requiredDocuments: stateProductRule?.requiredDocuments ?? productObj?.requiredDocuments,

                        leadId: existingLead?.id,

                        // default to state product rule first
                        pharmacyId: stateProductRule?.pharmacyId ?? productObj?.pharmacyId,

                        forms: subscriptionForms,
                        formsCount: subscriptionForms.length,

                        bundleShipmentCount: stateProductRule?.bundleShipmentCount ?? (productObj?.bundleShipmentCount ?? 1),
                        ordersPerEncounter: productObj?.ordersPerClinicianEncounter ?? 1,
                        bundlePaymentCount: billingFrequency, // for recurring billing for vouchers
                        bundlePaymentCouponCode: bundlePaymentCouponCode,

                        encounterType: productObj?.clinicianEncounterType ?? 'NONE',
                        encounterFrequency: productObj?.clinicianEncounterFrequency ?? 1,
                        encounterPrice: productObj?.clinicianEncounterPrice ?? 0.0,

                        upcharge: upcharge,
                        includesUpcharge: hasUpcharge,

                        testMode: testMode,

                        repId: profile?.repId ?? 'NONE'
                    };
                    newSubscription = await new Subscription(newSubscriptionFields).create();
                } else {
                    let updatedSubscriptionFields = {
                        status: 'PENDING',

                        userId: userId,
                        customer: new Customer({ ...profile }).toDto(),

                        // default to the state product rule first
                        coveredEntityId: stateProductRule?.practiceId ?? (productObj?.practiceId || null),

                        discountTotal: subscriptionOrderSummary?.discountTotal ?? 0,
                        discountTax: subscriptionOrderSummary?.discountTax ?? 0,

                        shippingTotal: subscriptionOrderSummary?.shippingTotal ?? 0,
                        shippingTax: subscriptionOrderSummary?.shippingTax ?? 0,

                        feeTotal: subscriptionOrderSummary?.feeTotal ?? 0,
                        feeTax: subscriptionOrderSummary?.feeTax ?? 0,

                        cartTotal: subscriptionOrderSummary?.cartTotal ?? 0,
                        cartTax: subscriptionOrderSummary?.cartTax ?? 0,

                        subtotal: subscriptionOrderSummary?.subtotal ?? 0,
                        tax: subscriptionOrderSummary?.tax ?? 0,
                        total: subscriptionOrderSummary?.total ?? 0,

                        lineItems: subscriptionLineItems,
                        discountLines: subscriptionDiscountLines,
                        shippingLines: subscriptionShippingLines,

                        shippingAddress: shippingAddress,
                        billingAddress: billingAddress,

                        customerIPAddress: ipAddress,
                        customerUserAgent: customerUserAgent,

                        customerNote: null,

                        billingPeriod: productObj?.subscriptionPeriod ?? "MONTH",
                        billingInterval: productObj?.subscriptionPeriodInterval ?? 1,
                        billingCycles: productObj?.subscriptionLength ?? 0,

                        source: existingLead?.source,
                        affiliateId: existingLead?.affiliateId,
                        subAffiliateId: existingLead?.subAffiliateId,
                        campaignId: existingLead?.campaignId,
                        programId: existingLead?.programId,
                        productSku: productObj?.sku ?? null,
                        productFamilyId: existingLead?.productFamilyId ?? productObj?.productFamilyId ?? null,
                        offerGroupId: existingLead?.offerGroupId ?? productObj?.offerGroupId ?? null,
                        offerToken: existingLead?.offerToken ?? null,
                        commercialEquivalencyGroupId: existingLead?.commercialEquivalencyGroupId ?? productObj?.commercialEquivalencyGroupId ?? null,
                        productId: productObj?.id ?? null,
                        variantId: variant?.id ?? null,

                        affiliateInfo: existingLead?.affiliateInfo,
                        deviceInfo: deviceInfo,
                        emailInfo: emailInfo,
                        phoneInfo: phoneInfo,

                        // default to the state product rule first
                        requiresConfirmation: stateProductRule?.requiresConfirmation ?? productObj?.requiresConfirmation,
                        requiresClinicianApproval: stateProductRule?.requiresClinicianApproval ?? productObj?.requiresClinicianApproval,
                        requiresClinicianEncounter: stateProductRule?.requiresClinicianEncounter ?? productObj?.requiresClinicianEncounter,
                        requiresSurveys: stateProductRule?.requiresSurveys ?? productObj?.requiresSurveys,

                        requiredDocuments: stateProductRule?.requiredDocuments ?? productObj?.requiredDocuments,

                        leadId: existingLead?.id,

                        // default to state product rule first
                        pharmacyId: stateProductRule?.pharmacyId ?? productObj?.pharmacyId,

                        forms: subscriptionForms,
                        formsCount: subscriptionForms.length,

                        bundleShipmentCount: stateProductRule?.bundleShipmentCount ?? (productObj?.bundleShipmentCount ?? 1),
                        ordersPerEncounter: productObj?.ordersPerClinicianEncounter ?? 1,
                        bundlePaymentCount: billingFrequency, // for recurring billing for vouchers
                        bundlePaymentCouponCode: bundlePaymentCouponCode,

                        encounterType: productObj?.clinicianEncounterType ?? 'NONE',
                        encounterFrequency: productObj?.clinicianEncounterFrequency ?? 1,
                        encounterPrice: productObj?.clinicianEncounterPrice ?? 0.0,

                        upcharge: upcharge,
                        includesUpcharge: hasUpcharge,

                        testMode: testMode,

                        repId: profile?.repId ?? 'NONE'
                    };
                    let updateSubscriptionSuccess = await Subscription.updateFields(newSubscription?.id, updatedSubscriptionFields);
                    if (isBooleanTrue(updateSubscriptionSuccess)) {
                        newSubscription = { ...newSubscription, ...updatedSubscriptionFields };
                    }
                }

                if (isObjectNotEmpty(newSubscription) && isStringNotEmpty(newSubscription.id) && isStringNotEmpty(newSubscription.userId) && isObjectNotEmpty(newSubscription.customer)) {
                    // create user note for enrollment in program
                    try {
                        await UserNote.create(userId, {
                            body: `Enrolled in ${program?.name} plan for ${productObj?.name} on ${fromUtcToTimeZone(newSubscription.createdAt, 'US/Pacific')}`,
                            at: newSubscription.createdAt,
                            by: userId
                        });
                    } catch (error) {
                        console.log(error);
                    }

                    let orderedAt = fromUtcToStartDate(moment.tz('US/Pacific'), 'US/Pacific');
                    // console.log('subscription for creating voucher order', newSubscription);
                    let orderLineItems = isListNotEmpty(newSubscription.lineItems) ? [...newSubscription.lineItems] : [];
                    let orderTaxLines = isListNotEmpty(newSubscription.taxLines) ? [...newSubscription.taxLines] : [];
                    let orderShippingLines = isListNotEmpty(newSubscription.shippingLines) ? [...newSubscription.shippingLines] : [];
                    let orderFeeLines = isListNotEmpty(newSubscription.feeLines) ? [...newSubscription.feeLines] : [];

                    let orderDiscountLines = [];
                    if (isListNotEmpty(newSubscription.discountLines)) {
                        for (const discountLine of newSubscription.discountLines) {
                            let subscriptionCoupon = await Coupon.getByCode(discountLine.code);
                            // console.log('subscriptionCoupon', subscriptionCoupon);
                            if (isObjectNotEmpty(subscriptionCoupon)) {
                                let subscriptionDiscountRes = Order.getOrderDiscount(subscriptionCoupon, [...orderLineItems], taxRate, newSubscription.bundlePaymentCount, userId, profile?.email, startingVariant, true);
                                let subscriptionDiscountTotal = subscriptionDiscountRes?.discountTotal ?? 0;
                                let subscriptionDiscountTax = subscriptionDiscountRes?.discountTax ?? 0;
                                let applyToBundle = isNumberGreaterThanOrEqualToZero(newSubscription.bundlePaymentCount) && newSubscription.bundlePaymentCount > 1 && isBooleanTrue(subscriptionCoupon?.applyToBundle) && isBooleanTrue(['RECURRING', 'BUNDLE', 'MIXED_RECURRING'].includes(subscriptionCoupon?.discountTarget));
                                let applyToBundleCount = isBooleanTrue(applyToBundle) ? newSubscription.bundlePaymentCount : 1;
                                orderDiscountLines.push(new OrderDiscountLine({
                                    code: subscriptionCoupon?.code,
                                    discount: subscriptionDiscountTotal * applyToBundleCount,
                                    discountTax: subscriptionDiscountTax * applyToBundleCount,
                                    discountTotal: (subscriptionDiscountTotal + subscriptionDiscountTax) * applyToBundleCount,
                                    recurring: isBooleanTrue(['RECURRING', 'BUNDLE', 'MIXED_RECURRING'].includes(subscriptionCoupon?.discountTarget))
                                }).toDto());
                            }
                        }
                    }

                    if (isNumberGreaterThanOrEqualToZero(newSubscription.bundlePaymentCount) && newSubscription.bundlePaymentCount > 1) {
                        let productBundleDiscounts = isListNotEmpty(product.bundleDiscounts) ? [...product.bundleDiscounts] : [];
                        let bundleDiscount = productBundleDiscounts.find(i => i.bundlePaymentCount === newSubscription.bundlePaymentCount);
                        if (isObjectNotEmpty(bundleDiscount)) {
                            let bundleCoupon = await Coupon.getByCode(bundleDiscount.couponCode);
                            // console.log('bundleCoupon', bundleCoupon);
                            if (isObjectNotEmpty(bundleCoupon)) {
                                let bundleDiscountRes = Order.getOrderDiscount(bundleCoupon, [...orderLineItems], taxRate, newSubscription.bundlePaymentCount, userId, profile?.email, startingVariant, true);
                                let bundleDiscountTotal = bundleDiscountRes?.discountTotal ?? 0;
                                let bundleDiscountTax = bundleDiscountRes?.discountTax ?? 0;
                                orderDiscountLines.push(new OrderDiscountLine({
                                    code: bundleCoupon?.code,
                                    discount: bundleDiscountTotal,
                                    discountTax: bundleDiscountTax,
                                    discountTotal: bundleDiscountTotal + bundleDiscountTax,
                                    recurring: true
                                }).toDto());

                                orderLineItems = orderLineItems.map((lineItem) => {
                                    let quantity = newSubscription.bundlePaymentCount;
                                    let subtotal = quantity * lineItem.price;
                                    let tax = quantity * lineItem.tax;
                                    let total = subtotal + tax;
                                    return new OrderLineItem({
                                        ...lineItem,
                                        quantity: newSubscription.bundlePaymentCount,
                                        subtotal: subtotal,
                                        tax: tax,
                                        total: total
                                    }).toDto();
                                });
                            }
                        }
                    }

                    let orderSummary = Order.calculateOrderSummary(orderLineItems, orderDiscountLines, orderFeeLines, orderShippingLines);

                    if (isObjectEmpty(firstOrder)) {
                        // create voucher order
                        let newOrderFields = {
                            orderedAt: orderedAt,
                            subscriptionId: newSubscription.id,
                            orderType: 'VOUCHER',

                            status: 'DRAFT',

                            userRefId: newSubscription.userRefId,
                            userId: newSubscription.userId,
                            customer: newSubscription.customer,

                            billingStatus: 'PENDING',
                            bypassBillingStatus: newSubscription.bypassBillingStatus,

                            billingAddress: isObjectNotEmpty(newSubscription.billingAddress) ? newSubscription.billingAddress : null,
                            shippingAddress: isObjectNotEmpty(newSubscription.shippingAddress) ? newSubscription.shippingAddress : null,

                            customerIPAddress: ipAddress,
                            customerUserAgent: customerUserAgent,
                            customerReferrerUrl: customerReferrerUrl,

                            customerNote: isStringNotEmpty(newSubscription.customerNote) ? newSubscription.customerNote : null,

                            discountTotal: orderSummary?.discountTotal ?? 0,
                            discountTax: orderSummary?.discountTax ?? 0,

                            shippingTotal: orderSummary?.shippingTotal ?? 0,
                            shippingTax: orderSummary?.shippingTax ?? 0,

                            feeTotal: orderSummary?.feeTotal ?? 0,
                            feeTax: orderSummary?.feeTax ?? 0,

                            cartTotal: orderSummary?.cartTotal ?? 0,
                            cartTax: orderSummary?.cartTax ?? 0,

                            subtotal: orderSummary?.subtotal ?? 0,
                            tax: orderSummary?.tax ?? 0,
                            total: orderSummary?.total ?? 0,

                            lineItems: orderLineItems,
                            taxLines: orderTaxLines,
                            shippingLines: orderShippingLines,
                            feeLines: orderFeeLines,
                            discountLines: orderDiscountLines,

                            shipmentStatus: 'PENDING',

                            requiresConfirmation: newSubscription?.requiresConfirmation ?? false,
                            requiresClinicianApproval: newSubscription?.requiresClinicianApproval ?? false,
                            requiresClinicianEncounter: newSubscription?.requiresClinicianEncounter ?? false,
                            requiresSurveys: newSubscription?.requiresSurveys ?? false,

                            requiredDocuments: newSubscription?.requiredDocuments ?? [],

                            source: isStringNotEmpty(newSubscription.source) ? newSubscription.source : null,
                            affiliateId: isStringNotEmpty(newSubscription.affiliateId) ? newSubscription.affiliateId : null,
                            subAffiliateId: isStringNotEmpty(newSubscription.subAffiliateId) ? newSubscription.subAffiliateId : null,
                            campaignId: isStringNotEmpty(newSubscription.campaignId) ? newSubscription.campaignId : null,
                            programId: isStringNotEmpty(newSubscription.programId) ? newSubscription.programId : null,

                            coveredEntityId: newSubscription?.coveredEntityId ?? null,
                            leadId: newSubscription?.leadId ?? null,

                            affiliateInfo: newSubscription?.affiliateInfo,
                            deviceInfo: newSubscription?.deviceInfo,
                            emailInfo: newSubscription?.emailInfo,
                            phoneInfo: newSubscription?.phoneInfo,

                            upcharge: upcharge,
                            includesUpcharge: hasUpcharge,

                            pharmacyId: isStringNotEmpty(newSubscription.pharmacyId) ? newSubscription.pharmacyId : null,

                            testMode: testMode,

                            repId: profile?.repId ?? 'NONE'
                        };

                        if ((isBooleanTrue(newSubscription.requiresConfirmation) && isBooleanTrue(isConfirmed)) ||
                            (isBooleanFalse(newSubscription.requiresConfirmation) && isBooleanTrue(newSubscription.requiresSurveys) && isBooleanTrue(isSurveysCompleted)) ||
                            (isBooleanFalse(newSubscription.requiresConfirmation) && isBooleanFalse(newSubscription.requiresSurveys))) {
                            newOrderFields.isConfirmed = isConfirmed;
                            newOrderFields.confirmedAt = moment().utc().toDate();
                            newOrderFields.status = 'CONFIRMED';
                        }

                        if (isBooleanTrue(newSubscription.requiresSurveys) && isBooleanTrue(isSurveysCompleted)) {
                            newOrderFields.isSurveysCompleted = isSurveysCompleted;
                            newOrderFields.surveysCompletedAt = moment().utc().toDate();
                        }

                        if (isListNotEmpty(newSubscription.requiredDocuments) && isBooleanTrue(isRequiredDocumentsProvided)) {
                            newOrderFields.isRequiredDocumentsProvided = isRequiredDocumentsProvided;
                            newOrderFields.requiredDocumentsProvidedAt = moment().utc().toDate();
                        }

                        // create order forms to save to order
                        newOrderFields.forms = isListNotEmpty(forms) ? forms.map((form) => {
                            return new OrderForm({ ...form }).toDto();
                        }) : [];
                        newOrderFields.formsCount = forms.length;

                        firstOrder = await new Order(newOrderFields).create();
                    } else {
                        let updatedOrderFields = {
                            orderedAt: orderedAt,
                            subscriptionId: newSubscription.id,
                            orderType: 'VOUCHER',

                            status: 'DRAFT',
                            statusLastUpdatedAt: moment().utc().toDate(),
                            statusLastUpdatedBy: 'SYSTEM',

                            userRefId: newSubscription.userRefId,
                            userId: newSubscription.userId,
                            customer: newSubscription.customer,

                            billingStatus: 'PENDING',
                            bypassBillingStatus: newSubscription.bypassBillingStatus,

                            billingAddress: isObjectNotEmpty(newSubscription.billingAddress) ? newSubscription.billingAddress : null,
                            shippingAddress: isObjectNotEmpty(newSubscription.shippingAddress) ? newSubscription.shippingAddress : null,

                            customerIPAddress: ipAddress,
                            customerUserAgent: customerUserAgent,
                            customerReferrerUrl: customerReferrerUrl,

                            customerNote: isStringNotEmpty(newSubscription.customerNote) ? newSubscription.customerNote : null,

                            discountTotal: orderSummary?.discountTotal ?? 0,
                            discountTax: orderSummary?.discountTax ?? 0,

                            shippingTotal: orderSummary?.shippingTotal ?? 0,
                            shippingTax: orderSummary?.shippingTax ?? 0,

                            feeTotal: orderSummary?.feeTotal ?? 0,
                            feeTax: orderSummary?.feeTax ?? 0,

                            cartTotal: orderSummary?.cartTotal ?? 0,
                            cartTax: orderSummary?.cartTax ?? 0,

                            subtotal: orderSummary?.subtotal ?? 0,
                            tax: orderSummary?.tax ?? 0,
                            total: orderSummary?.total ?? 0,

                            lineItems: orderLineItems,
                            taxLines: orderTaxLines,
                            shippingLines: orderShippingLines,
                            feeLines: orderFeeLines,
                            discountLines: orderDiscountLines,

                            shipmentStatus: 'PENDING',

                            requiresConfirmation: newSubscription?.requiresConfirmation ?? false,
                            requiresClinicianApproval: newSubscription?.requiresClinicianApproval ?? false,
                            requiresClinicianEncounter: newSubscription?.requiresClinicianEncounter ?? false,
                            requiresSurveys: newSubscription?.requiresSurveys ?? false,

                            requiredDocuments: newSubscription?.requiredDocuments ?? [],

                            source: isStringNotEmpty(newSubscription.source) ? newSubscription.source : null,
                            affiliateId: isStringNotEmpty(newSubscription.affiliateId) ? newSubscription.affiliateId : null,
                            subAffiliateId: isStringNotEmpty(newSubscription.subAffiliateId) ? newSubscription.subAffiliateId : null,
                            campaignId: isStringNotEmpty(newSubscription.campaignId) ? newSubscription.campaignId : null,
                            programId: isStringNotEmpty(newSubscription.programId) ? newSubscription.programId : null,

                            coveredEntityId: newSubscription?.coveredEntityId ?? null,
                            leadId: newSubscription?.leadId ?? null,

                            affiliateInfo: newSubscription?.affiliateInfo,
                            deviceInfo: newSubscription?.deviceInfo,
                            emailInfo: newSubscription?.emailInfo,
                            phoneInfo: newSubscription?.phoneInfo,

                            upcharge: upcharge,
                            includesUpcharge: hasUpcharge,

                            pharmacyId: isStringNotEmpty(newSubscription.pharmacyId) ? newSubscription.pharmacyId : null,

                            testMode: testMode,

                            repId: profile?.repId ?? 'NONE'
                        };

                        if ((isBooleanTrue(newSubscription.requiresConfirmation) && isBooleanTrue(isConfirmed)) ||
                            (isBooleanFalse(newSubscription.requiresConfirmation) && isBooleanTrue(newSubscription.requiresSurveys) && isBooleanTrue(isSurveysCompleted)) ||
                            (isBooleanFalse(newSubscription.requiresConfirmation) && isBooleanFalse(newSubscription.requiresSurveys))) {
                            updatedOrderFields.isConfirmed = isConfirmed;
                            updatedOrderFields.confirmedAt = moment().utc().toDate();
                            updatedOrderFields.status = 'CONFIRMED';
                        }

                        if (isBooleanTrue(newSubscription.requiresSurveys) && isBooleanTrue(isSurveysCompleted)) {
                            updatedOrderFields.isSurveysCompleted = isSurveysCompleted;
                            updatedOrderFields.surveysCompletedAt = moment().utc().toDate();
                        }

                        if (isListNotEmpty(newSubscription.requiredDocuments) && isBooleanTrue(isRequiredDocumentsProvided)) {
                            updatedOrderFields.isRequiredDocumentsProvided = isRequiredDocumentsProvided;
                            updatedOrderFields.requiredDocumentsProvidedAt = moment().utc().toDate();
                        }

                        // create order forms to save to order
                        updatedOrderFields.forms = isListNotEmpty(forms) ? forms.map((form) => {
                            return new OrderForm({ ...form }).toDto();
                        }) : [];
                        updatedOrderFields.formsCount = forms.length;

                        let updatedOrderSuccess = await Order.updateFields(firstOrder?.id, updatedOrderFields);
                        if (isBooleanTrue(updatedOrderSuccess)) {
                            firstOrder = { ...firstOrder, ...updatedOrderFields };
                        }
                    }

                    if (isObjectNotEmpty(firstOrder) && isStringNotEmpty(firstOrder.id)) {
                        // create prescription requests for the number of orders allowed per encounter based on subscription settings
                        // take answers from survey to determine where customer starts in titration schedule to help determine number of precription requests
                        // create the prescription request(s)
                        const encounterOrderCount = newSubscription?.ordersPerEncounter ?? 1;

                        let prescriptionRequests = [];

                        // titrationDirection - DECREASE, INCREASE, SAME_1, SAME_3
                        if (titrationDirection === 'DECREASE') {
                            let variant = availableVariants.find(i => i.titrationLevel === nextLevel);
                            if (isObjectNotEmpty(variant)) {
                                let variantPharmacyMapping = isListNotEmpty(variant?.supplierMappings) ? variant?.supplierMappings.find(i => i.supplierId === newSubscription?.pharmacyId) : null;
                                // console.log('variantPharmacyMapping', variantPharmacyMapping);
                                const newPrescriptionRequest = new PrescriptionRequest({
                                    name: product.name,
                                    strength: variant.strength,
                                    quantity: variantPharmacyMapping?.quantity ?? 1,
                                    refills: variantPharmacyMapping?.refills ?? 0,
                                    medRefId: variantPharmacyMapping?.refId ?? null,
                                    productId: variant.productId,
                                    variantId: variant.id
                                }).toDto();

                                prescriptionRequests.push(newPrescriptionRequest);
                            }
                        } else if (titrationDirection === 'SAME_1') {
                            let variant = availableVariants.find(i => i.titrationLevel === nextLevel);
                            if (isObjectNotEmpty(variant)) {
                                let variantPharmacyMapping = isListNotEmpty(variant?.supplierMappings) ? variant?.supplierMappings.find(i => i.supplierId === newSubscription?.pharmacyId) : null;
                                // console.log('variantPharmacyMapping', variantPharmacyMapping);
                                const newPrescriptionRequest = new PrescriptionRequest({
                                    name: product.name,
                                    strength: variant.strength,
                                    quantity: variantPharmacyMapping?.quantity ?? 1,
                                    refills: variantPharmacyMapping?.refills ?? 0,
                                    medRefId: variantPharmacyMapping?.refId ?? null,
                                    productId: variant.productId,
                                    variantId: variant.id
                                }).toDto();

                                prescriptionRequests.push(newPrescriptionRequest);
                            }
                        } else if (titrationDirection === 'SAME_3') {
                            let variant = availableVariants.find(i => i.titrationLevel === nextLevel);
                            if (isObjectNotEmpty(variant)) {
                                // console.log('pharmacyId', newSubscription?.pharmacyId);
                                let variantPharmacyMapping = isListNotEmpty(variant?.supplierMappings) ? variant?.supplierMappings.find(i => i.supplierId === newSubscription?.pharmacyId) : null;
                                // console.log('variantPharmacyMapping', variantPharmacyMapping);
                                const newPrescriptionRequest = new PrescriptionRequest({
                                    name: product.name,
                                    strength: variant.strength,
                                    quantity: variantPharmacyMapping?.quantity ?? 1,
                                    refills: encounterOrderCount - 1,
                                    medRefId: variantPharmacyMapping?.refId ?? null,
                                    productId: variant.productId,
                                    variantId: variant.id
                                }).toDto();

                                prescriptionRequests.push(newPrescriptionRequest);
                            }
                        } else {
                            if (startingLevel >= maxLevel) {
                                let variant = availableVariants.find(i => i.titrationLevel === nextLevel);
                                if (isObjectNotEmpty(variant)) {
                                    // console.log('pharmacyId', newSubscription?.pharmacyId);
                                    let variantPharmacyMapping = isListNotEmpty(variant?.supplierMappings) ? variant?.supplierMappings.find(i => i.supplierId === newSubscription?.pharmacyId) : null;
                                    // console.log('variantPharmacyMapping', variantPharmacyMapping);
                                    const newPrescriptionRequest = new PrescriptionRequest({
                                        name: product.name,
                                        strength: variant.strength,
                                        quantity: variantPharmacyMapping?.quantity ?? 1,
                                        refills: encounterOrderCount - 1,
                                        medRefId: variantPharmacyMapping?.refId ?? null,
                                        productId: variant.productId,
                                        variantId: variant.id
                                    }).toDto();

                                    prescriptionRequests.push(newPrescriptionRequest);
                                }
                            } else {
                                for (let i = 0; i < encounterOrderCount; i++) {
                                    let variant = availableVariants.find(i => i.titrationLevel === nextLevel);
                                    if (isObjectNotEmpty(variant)) {
                                        // console.log('variant', variant);

                                        if (i > 0 && isBooleanTrue(variant.requiresClinicianEncounter) && startingLevel !== maxLevel) {
                                            // if order other than the first order requires an encounter, then dont create the prescription and include it with this encounter for approval

                                            // escape the for loop
                                        } else {
                                            // console.log('pharmacyId', newSubscription?.pharmacyId);
                                            let variantPharmacyMapping = isListNotEmpty(variant?.supplierMappings) ? variant?.supplierMappings.find(i => i.supplierId === newSubscription?.pharmacyId) : null;
                                            // console.log('variantPharmacyMapping', variantPharmacyMapping);
                                            const newPrescriptionRequest = new PrescriptionRequest({
                                                name: product.name,
                                                strength: variant.strength,
                                                quantity: variantPharmacyMapping?.quantity ?? 1,
                                                refills: variantPharmacyMapping?.refills ?? 0,
                                                medRefId: variantPharmacyMapping?.refId ?? null,
                                                productId: variant.productId,
                                                variantId: variant.id
                                            }).toDto();

                                            prescriptionRequests.push(newPrescriptionRequest);

                                            if (nextLevel < maxLevel && variant.titrationPeriods === 1) {
                                                nextLevel = nextLevel + 1
                                            } else if (nextLevel < maxLevel && variant.titrationPeriods > 1) {
                                                // determine if level should increase or stay the same based on prescription count at this level
                                                if (prescriptionRequests.filter(i => i.variantId === variant.id).length >= variant.titrationPeriods) {
                                                    // increase level
                                                    nextLevel = nextLevel + 1;
                                                } else {
                                                    // stay at level
                                                    nextLevel = nextLevel;
                                                }
                                            } else if (nextLevel >= maxLevel) {
                                                nextLevel = nextLevel;
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        // console.log('prescriptionRequests', prescriptionRequests);

                        let newEncounter = isObjectNotEmpty(existingEncounter) ? { ...existingEncounter } : null;
                        if (isStringNotEmpty(productObj.clinicianEncounterType) && productObj.clinicianEncounterType !== 'NONE') {
                            let encounterForms = isListNotEmpty(forms) ? forms.map((form) => {
                                return new EncounterForm({ ...form }).toDto();
                            }) : [];

                            if (isObjectEmpty(newEncounter)) {
                                let newEncounterFields = {
                                    encounterType: productObj.clinicianEncounterType,
                                    visitType: 'CONSULT',

                                    userId: userId,
                                    customer: new Customer({ ...profile }).toDto(),

                                    practiceId: newSubscription?.coveredEntityId,

                                    status: 'CREATED',

                                    customerIPAddress: newSubscription?.customerIPAddress,
                                    customerUserAgent: newSubscription?.customerUserAgent,
                                    deviceInfo: newSubscription?.deviceInfo,

                                    orderIds: [firstOrder.id],
                                    subscriptionIds: [newSubscription.id],
                                    programId: newSubscription.programId,

                                    forms: encounterForms,
                                    formsCount: encounterForms.length,

                                    messageName: program?.name + ' Consultation',

                                    prescriptionRequests: prescriptionRequests,

                                    testMode: testMode,

                                    refId: uuid()
                                };

                                newEncounter = await new Encounter(newEncounterFields).create();
                            } else {
                                let updatedEncounterFields = {
                                    forms: encounterForms,
                                    formsCount: encounterForms.length,

                                    prescriptionRequests: prescriptionRequests,

                                    testMode: testMode,

                                    refId: uuid()
                                };

                                let updatedEncounterSuccess = await Encounter.updateFields(newEncounter.id, updatedEncounterFields);
                                if (isBooleanTrue(updatedEncounterSuccess)) {
                                    newEncounter = { ...newEncounter, ...updatedEncounterFields };
                                }
                            }
                        }

                        if (isObjectNotEmpty(newEncounter) && isStringNotEmpty(newEncounter.id)) {
                            // update subscription, order, and lead

                            // update lead
                            let updatedLead = isObjectNotEmpty(existingLead) ? { ...existingLead } : null;
                            if (isObjectNotEmpty(updatedLead)) {
                                let updatedLeadFields = {
                                    subscriptionIds: [newSubscription.id],
                                    orderIds: [firstOrder.id],
                                    encounterIds: [newEncounter.id],
                                    status: 'PENDING',
                                    statusHistory: StatusHistory.addNewStatus('PENDING', updatedLead.statusHistory),
                                    acceptConsentAgreements: true,
                                    shippingState: shippingAddress?.state,
                                    shippingAddress: shippingAddress,
                                    billingAddress: billingAddress,

                                    testMode: testMode
                                };
                                let updatedLeadSuccess2 = await Lead.updateFields(updatedLead.id, updatedLeadFields);
                                if (isBooleanTrue(updatedLeadSuccess2)) {
                                    // console.log('updatedLeadSuccess2', updatedLeadSuccess2);
                                    updatedLead = { ...updatedLead, ...updatedLeadFields };
                                }
                            }

                            // update subscription
                            let oldRelatedOrderIds = isListNotEmpty(newSubscription.relatedOrderIds) ? [...newSubscription.relatedOrderIds] : [];
                            let updatedRelatedOrderIds = [...oldRelatedOrderIds, firstOrder.id];
                            let updatedSubscriptionFields = {
                                relatedOrderIds: updatedRelatedOrderIds
                            };
                            // don't set payment dates on first order since the transaction is a authorization. 
                            // Payment dates will be set once the authorization is captured
                            let updatedSubscriptionSuccess2 = await Subscription.updateFields(newSubscription.id, updatedSubscriptionFields);
                            if (isBooleanTrue(updatedSubscriptionSuccess2)) {
                                newSubscription = { ...newSubscription, ...updatedSubscriptionFields };
                            }

                            let updatedOrderSuccess2 = await Order.updateField(firstOrder?.id, 'encounterId', newEncounter.id);
                            if (isBooleanTrue(updatedOrderSuccess2)) {
                                // console.log('updatedOrderSuccess2', updatedOrderSuccess2);
                                firstOrder = { ...firstOrder, encounterId: newEncounter?.id };
                            }

                            return { order: { ...firstOrder }, subscription: { ...newSubscription }, encounter: { ...newEncounter }, lead: { ...updatedLead } };
                        }
                    }
                }
            } catch (error) {
                console.log(error);
                throw new Error(error.message);
            }
        }

        return { order: null, subscription: null, encounter: null, lead: null };
    }

    static startPrescriptionSubscriptions = async (userId, profile, forms, cartSummary, cartItems, coupon, taxRate, shippingMethod, shippingAddress, billingAddress, ipAddress, deviceInfo, emailInfo, phoneInfo, affiliateInfo, source, affiliateId, subAffiliateId, campaign, program, existingSubscriptions = [], existingOrders = [], existingEncounters = [], existingLead, isConfirmed = false, isSurveysCompleted = false, isRequiredDocumentsProvided = false, testMode = false) => {
        // this is the order that the auth transaction will be associated with and is passed back from this method with the subscription
        if (isStringNotEmpty(userId) && isObjectNotEmpty(profile) && isObjectNotEmpty(program) && isListNotEmpty(forms) && isListNotEmpty(cartItems)) {
            let customerUserAgent = getClientUserAgent();
            let customerReferrerUrl = getReferrerUrl();

            let products = await Product.getAll();
            let variants = await ProductVariant.getAll();

            try {
                let allLineItems = [];
                // ASSUMPTION: quantity = 1
                if (isListNotEmpty(cartItems)) {
                    for (const cartItem of cartItems) {
                        const cartItemProductObj = getProductObjById(cartItem?.id, products);
                        const cartItemVariantObj = getVariantObjById(cartItem?.variantId, variants);
                        let quantity = cartItem.quantity;
                        let unitPrice = cartItemVariantObj.getPrice();
                        let lineItemSubtotal = quantity * unitPrice;
                        let lineItemTax = Order.getLineItemTaxTotal(cartItemProductObj?.toDto(), quantity, taxRate);
                        let lineItemTotal = lineItemSubtotal + lineItemTax;
                        allLineItems.push(new OrderLineItem({
                            name: cartItemProductObj.name,
                            sku: cartItemProductObj.sku,
                            productId: cartItemProductObj.id,
                            variantId: cartItemVariantObj?.id ?? null,
                            quantity: quantity,
                            price: unitPrice,
                            subtotal: lineItemSubtotal,
                            tax: lineItemTax,
                            total: lineItemTotal
                        }).toDto());
                    }
                }

                let orders = isListNotEmpty(existingOrders) ? [...existingOrders] : [];
                let subscriptions = isListNotEmpty(existingSubscriptions) ? [...existingSubscriptions] : [];
                let encounters = isListNotEmpty(existingEncounters) ? [...existingEncounters] : [];
                for (const cartItem of cartItems) {
                    let subscriptionLineItems = [];

                    // ASSUMPTION: quantity = 1
                    if (isObjectNotEmpty(cartItem) && isStringNotEmpty(cartItem.id)) {
                        const productObj = getProductObjById(cartItem?.id, products);
                        const variantObj = getVariantObjById(cartItem?.variantId, variants);

                        let hasUpcharge = false;
                        let upcharge = 0;
                        if (isNotNullOrUndefined(variantObj) && isNumberNotEmpty(variantObj?.upcharge)) {
                            if (variantObj?.upcharge > 0) {
                                hasUpcharge = true;
                                upcharge = variantObj?.upcharge;
                            }
                        }

                        let quantity = cartItem.quantity;
                        let unitPrice = variantObj.getPrice();
                        let lineItemSubtotal = quantity * unitPrice;
                        let lineItemTax = Order.getLineItemTaxTotal(productObj?.toDto(), quantity, taxRate);
                        let lineItemTotal = lineItemSubtotal + lineItemTax;
                        subscriptionLineItems.push(new OrderLineItem({
                            name: productObj.name,
                            sku: productObj.sku,
                            productId: productObj.id,
                            variantId: variantObj?.id ?? null,
                            quantity: quantity,
                            price: unitPrice,
                            subtotal: lineItemSubtotal,
                            tax: lineItemTax,
                            total: lineItemTotal
                        }).toDto());

                        let subscriptionDiscountLines = [];
                        if (isObjectNotEmpty(coupon) && isStringNotEmpty(coupon.code)) {
                            let discountRes = Order.getPartialOrderDiscount(coupon, allLineItems, subscriptionLineItems, taxRate, 1, userId, profile?.email, cartItem.variant, true);
                            // console.log('discountRes', discountRes);
                            let discountTotal = discountRes?.discountTotal ?? 0;
                            let discountTax = discountRes?.discountTax ?? 0;
                            // the subscription stores one month; each order multiplies it by bundlePaymentCount
                            subscriptionDiscountLines.push(new OrderDiscountLine({
                                code: coupon?.code,
                                discount: discountTotal,
                                discountTax: discountTax,
                                discountTotal: discountTotal + discountTax,
                                recurring: isBooleanTrue(['RECURRING', 'BUNDLE', 'MIXED_RECURRING'].includes(coupon?.discountTarget))
                            }).toDto());

                            if (isNotNullOrUndefined(variantObj) && isNumberNotEmpty(variantObj?.upcharge)) {
                                if (variantObj?.upcharge > 0 && (coupon.discountTarget === 'RECURRING' || coupon.discountTarget === 'MIXED_RECURRING')) {
                                    upcharge = upcharge - (discountTotal * (upcharge / variantObj.getPrice()));
                                }
                            }
                        }

                        let bundlePaymentCouponCode = null;
                        if (isNumberGreaterThanOrEqualToZero(cartItem.bundleCount) && cartItem.bundleCount > 1) {
                            let productBundleDiscounts = isListNotEmpty(productObj?.bundleDiscounts) ? [...productObj.bundleDiscounts] : [];
                            let bundleDiscount = productBundleDiscounts.find(i => i.bundlePaymentCount === cartItem.bundleCount);
                            if (isObjectNotEmpty(bundleDiscount)) {
                                let bundleCoupon = await Coupon.getByCode(bundleDiscount.couponCode);
                                if (isObjectNotEmpty(bundleCoupon)) {
                                    bundlePaymentCouponCode = bundleDiscount.couponCode;
                                    // let bundleDiscountRes = Order.getOrderDiscount(bundleCoupon, [...lineItems], taxRate, cartItem.bundleCount);
                                    // let bundleDiscountTotal = bundleDiscountRes?.discountTotal ?? 0;
                                    // let bundleDiscountTax = bundleDiscountRes?.discountTax ?? 0;
                                    // discountLines.push(new OrderDiscountLine({
                                    //     code: bundleCoupon?.code,
                                    //     discount: bundleDiscountTotal,
                                    //     discountTax: bundleDiscountTax,
                                    //     discountTotal: bundleDiscountTotal + bundleDiscountTax,
                                    //     recurring: true
                                    // }).toDto());
                                }
                            }
                        }

                        let subscriptionShippingLines = [];
                        if (isObjectNotEmpty(shippingMethod)) {
                            let shippingMethodCostRes = CartItem.calculateShippingMethodCost(shippingMethod, isListNotEmpty(cartItems) ? [...cartItems] : [], taxRate, products, variants);
                            subscriptionShippingLines.push(new OrderShippingLine({
                                methodTitle: shippingMethod.name,
                                methodId: shippingMethod.id,
                                taxStatus: shippingMethod.taxStatus,
                                subtotal: shippingMethodCostRes.shippingTotal,
                                tax: shippingMethodCostRes.shippingTax,
                                total: shippingMethodCostRes.shippingTotal + shippingMethodCostRes.shippingTax
                            }).toDto());
                        }

                        let subscriptionOrderSummary = Order.calculateOrderSummary(subscriptionLineItems, subscriptionDiscountLines, [], subscriptionShippingLines);
                        // console.log('subscriptionOrderSummary', subscriptionOrderSummary);
                        let stateProductRule = productObj.productRules.find(i => i.state === shippingAddress?.state);

                        // save forms to subscription
                        let subscriptionForms = isListNotEmpty(forms) ? forms.map((form) => {
                            return new SubscriptionForm({ ...form }).toDto();
                        }) : [];

                        let existingSubscription = existingSubscriptions.find(i => i.lineItems.some(j => j.productId === cartItem.id && j.variantId === cartItem.variantId));
                        // console.log('existingSubscriptions', existingSubscriptions);
                        // console.log('existingSubscription', existingSubscription);
                        // console.log('cartItem', cartItem);
                        let newSubscription = isObjectNotEmpty(existingSubscription) ? { ...existingSubscription } : null;
                        if (isObjectEmpty(newSubscription)) {
                            // create the parent order that will trigger the subscription to be created
                            let newSubscriptionFields = {
                                status: 'CREATED',

                                userId: userId,
                                customer: new Customer({ ...profile }).toDto(),

                                // default to the state product rule first
                                coveredEntityId: stateProductRule?.practiceId ?? (productObj?.practiceId || null),

                                discountTotal: subscriptionOrderSummary?.discountTotal ?? 0,
                                discountTax: subscriptionOrderSummary?.discountTax ?? 0,

                                shippingTotal: subscriptionOrderSummary?.shippingTotal ?? 0,
                                shippingTax: subscriptionOrderSummary?.shippingTax ?? 0,

                                feeTotal: subscriptionOrderSummary?.feeTotal ?? 0,
                                feeTax: subscriptionOrderSummary?.feeTax ?? 0,

                                cartTotal: subscriptionOrderSummary?.cartTotal ?? 0,
                                cartTax: subscriptionOrderSummary?.cartTax ?? 0,

                                subtotal: subscriptionOrderSummary?.subtotal ?? 0,
                                tax: subscriptionOrderSummary?.tax ?? 0,
                                total: subscriptionOrderSummary?.total ?? 0,

                                lineItems: subscriptionLineItems,
                                discountLines: subscriptionDiscountLines,
                                shippingLines: subscriptionShippingLines,

                                shippingAddress: shippingAddress,
                                billingAddress: billingAddress,

                                customerIPAddress: ipAddress,
                                customerUserAgent: customerUserAgent,

                                customerNote: null,

                                billingPeriod: productObj?.subscriptionPeriod ?? "MONTH",
                                billingInterval: productObj?.subscriptionPeriodInterval ?? 1,
                                billingCycles: productObj?.subscriptionLength ?? 0,

                                source: existingLead?.source,
                                affiliateId: existingLead?.affiliateId,
                                subAffiliateId: existingLead?.subAffiliateId,
                                campaignId: existingLead?.campaignId,
                                programId: existingLead?.programId,
                                productSku: productObj?.sku ?? null,
                                productFamilyId: existingLead?.productFamilyId ?? productObj?.productFamilyId ?? null,
                                offerGroupId: existingLead?.offerGroupId ?? productObj?.offerGroupId ?? null,
                                offerToken: existingLead?.offerToken ?? null,
                                commercialEquivalencyGroupId: existingLead?.commercialEquivalencyGroupId ?? productObj?.commercialEquivalencyGroupId ?? null,
                                productId: productObj?.id ?? null,
                                variantId: variantObj?.id ?? null,

                                affiliateInfo: existingLead?.affiliateInfo,
                                deviceInfo: deviceInfo,
                                emailInfo: emailInfo,
                                phoneInfo: phoneInfo,

                                // default to the state product rule first
                                requiresConfirmation: stateProductRule?.requiresConfirmation ?? productObj?.requiresConfirmation,
                                requiresClinicianApproval: stateProductRule?.requiresClinicianApproval ?? productObj?.requiresClinicianApproval,
                                requiresClinicianEncounter: stateProductRule?.requiresClinicianEncounter ?? productObj?.requiresClinicianEncounter,
                                requiresSurveys: stateProductRule?.requiresSurveys ?? productObj?.requiresSurveys,

                                requiredDocuments: stateProductRule?.requiredDocuments ?? productObj?.requiredDocuments,

                                leadId: existingLead?.id,

                                // default to state product rule first
                                pharmacyId: stateProductRule?.pharmacyId ?? productObj?.pharmacyId,

                                forms: subscriptionForms,
                                formsCount: subscriptionForms.length,

                                bundleShipmentCount: stateProductRule?.bundleShipmentCount ?? (productObj?.bundleShipmentCount ?? 1),
                                ordersPerEncounter: productObj?.ordersPerClinicianEncounter ?? 1,
                                bundlePaymentCount: cartItem.bundleCount ?? 1, // for recurring billing for vouchers
                                bundlePaymentCouponCode: bundlePaymentCouponCode,

                                encounterType: productObj?.clinicianEncounterType ?? 'NONE',
                                encounterFrequency: productObj?.clinicianEncounterFrequency ?? 1,
                                encounterPrice: productObj?.clinicianEncounterPrice ?? 0.0,

                                upcharge: upcharge,
                                includesUpcharge: hasUpcharge,

                                testMode: testMode,

                                repId: profile?.repId ?? 'NONE'
                            };
                            newSubscription = await new Subscription(newSubscriptionFields).create();
                        } else {
                            let updatedSubscriptionFields = {
                                status: 'CREATED',

                                userId: userId,
                                customer: new Customer({ ...profile }).toDto(),

                                // default to the state product rule first
                                coveredEntityId: stateProductRule?.practiceId ?? (productObj?.practiceId || null),

                                discountTotal: subscriptionOrderSummary?.discountTotal ?? 0,
                                discountTax: subscriptionOrderSummary?.discountTax ?? 0,

                                shippingTotal: subscriptionOrderSummary?.shippingTotal ?? 0,
                                shippingTax: subscriptionOrderSummary?.shippingTax ?? 0,

                                feeTotal: subscriptionOrderSummary?.feeTotal ?? 0,
                                feeTax: subscriptionOrderSummary?.feeTax ?? 0,

                                cartTotal: subscriptionOrderSummary?.cartTotal ?? 0,
                                cartTax: subscriptionOrderSummary?.cartTax ?? 0,

                                subtotal: subscriptionOrderSummary?.subtotal ?? 0,
                                tax: subscriptionOrderSummary?.tax ?? 0,
                                total: subscriptionOrderSummary?.total ?? 0,

                                lineItems: subscriptionLineItems,
                                discountLines: subscriptionDiscountLines,
                                shippingLines: subscriptionShippingLines,

                                shippingAddress: shippingAddress,
                                billingAddress: billingAddress,

                                customerIPAddress: ipAddress,
                                customerUserAgent: customerUserAgent,

                                customerNote: null,

                                billingPeriod: productObj?.subscriptionPeriod ?? "MONTH",
                                billingInterval: productObj?.subscriptionPeriodInterval ?? 1,
                                billingCycles: productObj?.subscriptionLength ?? 0,

                                source: existingLead?.source,
                                affiliateId: existingLead?.affiliateId,
                                subAffiliateId: existingLead?.subAffiliateId,
                                campaignId: existingLead?.campaignId,
                                programId: existingLead?.programId,
                                productSku: productObj?.sku ?? null,
                                productFamilyId: existingLead?.productFamilyId ?? productObj?.productFamilyId ?? null,
                                offerGroupId: existingLead?.offerGroupId ?? productObj?.offerGroupId ?? null,
                                offerToken: existingLead?.offerToken ?? null,
                                commercialEquivalencyGroupId: existingLead?.commercialEquivalencyGroupId ?? productObj?.commercialEquivalencyGroupId ?? null,
                                productId: productObj?.id ?? null,
                                variantId: variantObj?.id ?? null,

                                affiliateInfo: existingLead?.affiliateInfo,
                                deviceInfo: deviceInfo,
                                emailInfo: emailInfo,
                                phoneInfo: phoneInfo,

                                // default to the state product rule first
                                requiresConfirmation: stateProductRule?.requiresConfirmation ?? productObj?.requiresConfirmation,
                                requiresClinicianApproval: stateProductRule?.requiresClinicianApproval ?? productObj?.requiresClinicianApproval,
                                requiresClinicianEncounter: stateProductRule?.requiresClinicianEncounter ?? productObj?.requiresClinicianEncounter,
                                requiresSurveys: stateProductRule?.requiresSurveys ?? productObj?.requiresSurveys,

                                requiredDocuments: stateProductRule?.requiredDocuments ?? productObj?.requiredDocuments,

                                leadId: existingLead?.id,

                                // default to state product rule first
                                pharmacyId: stateProductRule?.pharmacyId ?? productObj?.pharmacyId,

                                forms: subscriptionForms,
                                formsCount: subscriptionForms.length,

                                bundleShipmentCount: stateProductRule?.bundleShipmentCount ?? (productObj?.bundleShipmentCount ?? 1),
                                ordersPerEncounter: productObj?.ordersPerClinicianEncounter ?? 1,
                                bundlePaymentCount: cartItem.bundleCount, // for recurring billing for vouchers
                                bundlePaymentCouponCode: bundlePaymentCouponCode,

                                encounterType: productObj?.clinicianEncounterType ?? 'NONE',
                                encounterFrequency: productObj?.clinicianEncounterFrequency ?? 1,
                                encounterPrice: productObj?.clinicianEncounterPrice ?? 0.0,

                                upcharge: upcharge,
                                includesUpcharge: hasUpcharge,

                                testMode: testMode,

                                repId: profile?.repId ?? 'NONE'
                            };
                            let updateSubscriptionSuccess = await Subscription.updateFields(newSubscription?.id, updatedSubscriptionFields);
                            if (isBooleanTrue(updateSubscriptionSuccess)) {
                                newSubscription = { ...newSubscription, ...updatedSubscriptionFields };
                            }
                        }

                        if (isObjectNotEmpty(newSubscription) && isStringNotEmpty(newSubscription.id) && isStringNotEmpty(newSubscription.userId) && isObjectNotEmpty(newSubscription.customer)) {
                            subscriptions = updateRecord('id', { ...newSubscription }, [...subscriptions]);
                            // create user note for enrollment in program
                            try {
                                await UserNote.create(userId, {
                                    body: `Enrolled in ${program?.name} plan for ${productObj?.name} on ${fromUtcToTimeZone(newSubscription.createdAt, 'US/Pacific')}`,
                                    at: newSubscription.createdAt,
                                    by: userId
                                });
                            } catch (error) {
                                console.log(error);
                            }

                            let orderedAt = fromUtcToStartDate(moment.tz('US/Pacific'), 'US/Pacific');
                            // console.log('subscription for creating voucher order', newSubscription);
                            let orderLineItems = isListNotEmpty(newSubscription.lineItems) ? [...newSubscription.lineItems] : [];
                            let orderTaxLines = isListNotEmpty(newSubscription.taxLines) ? [...newSubscription.taxLines] : [];
                            let orderShippingLines = isListNotEmpty(newSubscription.shippingLines) ? [...newSubscription.shippingLines] : [];
                            let orderFeeLines = isListNotEmpty(newSubscription.feeLines) ? [...newSubscription.feeLines] : [];

                            let orderDiscountLines = [];
                            if (isListNotEmpty(newSubscription.discountLines)) {
                                for (const discountLine of newSubscription.discountLines) {
                                    let subscriptionCoupon = await Coupon.getByCode(discountLine.code);
                                    console.log('subscriptionCoupon', subscriptionCoupon);
                                    if (isObjectNotEmpty(subscriptionCoupon)) {
                                        let subscriptionDiscountRes = Order.getPartialOrderDiscount(subscriptionCoupon, [...allLineItems], [...orderLineItems], taxRate, newSubscription.bundlePaymentCount, userId, profile?.email, cartItem.variant, true);
                                        let subscriptionDiscountTotal = subscriptionDiscountRes?.discountTotal ?? 0;
                                        let subscriptionDiscountTax = subscriptionDiscountRes?.discountTax ?? 0;
                                        let applyToBundle = isNumberGreaterThanOrEqualToZero(newSubscription.bundlePaymentCount) && newSubscription.bundlePaymentCount > 1 && isBooleanTrue(subscriptionCoupon?.applyToBundle) && isBooleanTrue(['RECURRING', 'BUNDLE', 'MIXED_RECURRING'].includes(subscriptionCoupon?.discountTarget));
                                        let applyToBundleCount = isBooleanTrue(applyToBundle) ? newSubscription.bundlePaymentCount : 1;
                                        orderDiscountLines.push(new OrderDiscountLine({
                                            code: subscriptionCoupon?.code,
                                            discount: subscriptionDiscountTotal * applyToBundleCount,
                                            discountTax: subscriptionDiscountTax * applyToBundleCount,
                                            discountTotal: (subscriptionDiscountTotal + subscriptionDiscountTax) * applyToBundleCount,
                                            recurring: isBooleanTrue(['RECURRING', 'BUNDLE', 'MIXED_RECURRING'].includes(subscriptionCoupon?.discountTarget))
                                        }).toDto());
                                    }
                                }
                            }

                            if (isNumberGreaterThanOrEqualToZero(newSubscription.bundlePaymentCount) && newSubscription.bundlePaymentCount > 1) {
                                let productBundleDiscounts = isListNotEmpty(productObj?.bundleDiscounts) ? [...productObj?.bundleDiscounts] : [];
                                let bundleDiscount = productBundleDiscounts.find(i => i.bundlePaymentCount === newSubscription.bundlePaymentCount);
                                if (isObjectNotEmpty(bundleDiscount)) {
                                    let bundleCoupon = await Coupon.getByCode(bundleDiscount.couponCode);
                                    // console.log('bundleCoupon', bundleCoupon);
                                    if (isObjectNotEmpty(bundleCoupon)) {
                                        let bundleDiscountRes = Order.getPartialOrderDiscount(bundleCoupon, [...allLineItems], [...orderLineItems], taxRate, newSubscription.bundlePaymentCount, userId, profile?.email, cartItem.variant, true);
                                        let bundleDiscountTotal = bundleDiscountRes?.discountTotal ?? 0;
                                        let bundleDiscountTax = bundleDiscountRes?.discountTax ?? 0;
                                        orderDiscountLines.push(new OrderDiscountLine({
                                            code: bundleCoupon?.code,
                                            discount: bundleDiscountTotal,
                                            discountTax: bundleDiscountTax,
                                            discountTotal: bundleDiscountTotal + bundleDiscountTax,
                                            recurring: true
                                        }).toDto());

                                        orderLineItems = orderLineItems.map((lineItem) => {
                                            let quantity = newSubscription.bundlePaymentCount;
                                            let subtotal = quantity * lineItem.price;
                                            let tax = quantity * lineItem.tax;
                                            let total = subtotal + tax;
                                            return new OrderLineItem({
                                                ...lineItem,
                                                quantity: newSubscription.bundlePaymentCount,
                                                subtotal: subtotal,
                                                tax: tax,
                                                total: total
                                            }).toDto();
                                        });
                                    }
                                }
                            }

                            let orderSummary = Order.calculateOrderSummary(orderLineItems, orderDiscountLines, orderFeeLines, orderShippingLines);

                            let existingOrder = existingOrders.find(i => i.lineItems.some(j => j.productId === cartItem.id && j.variantId === cartItem.variantId));
                            let newOrder = isObjectNotEmpty(existingOrder) ? { ...existingOrder } : null;
                            if (isObjectEmpty(newOrder)) {
                                // create voucher order
                                let newOrderFields = {
                                    orderedAt: orderedAt,
                                    subscriptionId: newSubscription.id,
                                    orderType: 'VOUCHER',

                                    status: 'DRAFT',

                                    userRefId: newSubscription.userRefId,
                                    userId: newSubscription.userId,
                                    customer: newSubscription.customer,

                                    billingStatus: 'PENDING',
                                    bypassBillingStatus: newSubscription.bypassBillingStatus,

                                    billingAddress: isObjectNotEmpty(newSubscription.billingAddress) ? newSubscription.billingAddress : null,
                                    shippingAddress: isObjectNotEmpty(newSubscription.shippingAddress) ? newSubscription.shippingAddress : null,

                                    customerIPAddress: ipAddress,
                                    customerUserAgent: customerUserAgent,
                                    customerReferrerUrl: customerReferrerUrl,

                                    customerNote: isStringNotEmpty(newSubscription.customerNote) ? newSubscription.customerNote : null,

                                    discountTotal: orderSummary?.discountTotal ?? 0,
                                    discountTax: orderSummary?.discountTax ?? 0,

                                    shippingTotal: orderSummary?.shippingTotal ?? 0,
                                    shippingTax: orderSummary?.shippingTax ?? 0,

                                    feeTotal: orderSummary?.feeTotal ?? 0,
                                    feeTax: orderSummary?.feeTax ?? 0,

                                    cartTotal: orderSummary?.cartTotal ?? 0,
                                    cartTax: orderSummary?.cartTax ?? 0,

                                    subtotal: orderSummary?.subtotal ?? 0,
                                    tax: orderSummary?.tax ?? 0,
                                    total: orderSummary?.total ?? 0,

                                    lineItems: orderLineItems,
                                    taxLines: orderTaxLines,
                                    shippingLines: orderShippingLines,
                                    feeLines: orderFeeLines,
                                    discountLines: orderDiscountLines,

                                    shipmentStatus: 'PENDING',

                                    requiresConfirmation: newSubscription?.requiresConfirmation ?? false,
                                    requiresClinicianApproval: newSubscription?.requiresClinicianApproval ?? false,
                                    requiresClinicianEncounter: newSubscription?.requiresClinicianEncounter ?? false,
                                    requiresSurveys: newSubscription?.requiresSurveys ?? false,

                                    requiredDocuments: newSubscription?.requiredDocuments ?? [],

                                    source: isStringNotEmpty(newSubscription.source) ? newSubscription.source : null,
                                    affiliateId: isStringNotEmpty(newSubscription.affiliateId) ? newSubscription.affiliateId : null,
                                    subAffiliateId: isStringNotEmpty(newSubscription.subAffiliateId) ? newSubscription.subAffiliateId : null,
                                    campaignId: isStringNotEmpty(newSubscription.campaignId) ? newSubscription.campaignId : null,
                                    programId: isStringNotEmpty(newSubscription.programId) ? newSubscription.programId : null,

                                    coveredEntityId: newSubscription?.coveredEntityId ?? null,
                                    leadId: newSubscription?.leadId ?? null,

                                    affiliateInfo: newSubscription?.affiliateInfo,
                                    deviceInfo: newSubscription?.deviceInfo,
                                    emailInfo: newSubscription?.emailInfo,
                                    phoneInfo: newSubscription?.phoneInfo,

                                    upcharge: upcharge,
                                    includesUpcharge: hasUpcharge,

                                    pharmacyId: isStringNotEmpty(newSubscription.pharmacyId) ? newSubscription.pharmacyId : null,


                                    testMode: testMode,

                                    repId: profile?.repId ?? 'NONE'
                                };

                                if ((isBooleanTrue(newSubscription.requiresConfirmation) && isBooleanTrue(isConfirmed)) ||
                                    (isBooleanFalse(newSubscription.requiresConfirmation) && isBooleanTrue(newSubscription.requiresSurveys) && isBooleanTrue(isSurveysCompleted)) ||
                                    (isBooleanFalse(newSubscription.requiresConfirmation) && isBooleanFalse(newSubscription.requiresSurveys))) {
                                    newOrderFields.isConfirmed = isConfirmed;
                                    newOrderFields.confirmedAt = moment().utc().toDate();
                                    newOrderFields.status = 'CONFIRMED';
                                }

                                if (isBooleanTrue(newSubscription.requiresSurveys) && isBooleanTrue(isSurveysCompleted)) {
                                    newOrderFields.isSurveysCompleted = isSurveysCompleted;
                                    newOrderFields.surveysCompletedAt = moment().utc().toDate();
                                }

                                if (isListNotEmpty(newSubscription.requiredDocuments) && isBooleanTrue(isRequiredDocumentsProvided)) {
                                    newOrderFields.isRequiredDocumentsProvided = isRequiredDocumentsProvided;
                                    newOrderFields.requiredDocumentsProvidedAt = moment().utc().toDate();
                                }

                                // create order forms to save to order
                                newOrderFields.forms = isListNotEmpty(forms) ? forms.map((form) => {
                                    return new OrderForm({ ...form }).toDto();
                                }) : [];
                                newOrderFields.formsCount = forms.length;

                                newOrder = await new Order(newOrderFields).create();
                            } else {
                                let updatedOrderFields = {
                                    orderedAt: orderedAt,
                                    subscriptionId: newSubscription.id,
                                    orderType: 'VOUCHER',

                                    status: 'DRAFT',
                                    statusLastUpdatedAt: moment().utc().toDate(),
                                    statusLastUpdatedBy: 'SYSTEM',

                                    userRefId: newSubscription.userRefId,
                                    userId: newSubscription.userId,
                                    customer: newSubscription.customer,

                                    billingStatus: 'PENDING',
                                    bypassBillingStatus: newSubscription.bypassBillingStatus,

                                    billingAddress: isObjectNotEmpty(newSubscription.billingAddress) ? newSubscription.billingAddress : null,
                                    shippingAddress: isObjectNotEmpty(newSubscription.shippingAddress) ? newSubscription.shippingAddress : null,

                                    customerIPAddress: ipAddress,
                                    customerUserAgent: customerUserAgent,
                                    customerReferrerUrl: customerReferrerUrl,

                                    customerNote: isStringNotEmpty(newSubscription.customerNote) ? newSubscription.customerNote : null,

                                    discountTotal: orderSummary?.discountTotal ?? 0,
                                    discountTax: orderSummary?.discountTax ?? 0,

                                    shippingTotal: orderSummary?.shippingTotal ?? 0,
                                    shippingTax: orderSummary?.shippingTax ?? 0,

                                    feeTotal: orderSummary?.feeTotal ?? 0,
                                    feeTax: orderSummary?.feeTax ?? 0,

                                    cartTotal: orderSummary?.cartTotal ?? 0,
                                    cartTax: orderSummary?.cartTax ?? 0,

                                    subtotal: orderSummary?.subtotal ?? 0,
                                    tax: orderSummary?.tax ?? 0,
                                    total: orderSummary?.total ?? 0,

                                    lineItems: orderLineItems,
                                    taxLines: orderTaxLines,
                                    shippingLines: orderShippingLines,
                                    feeLines: orderFeeLines,
                                    discountLines: orderDiscountLines,

                                    shipmentStatus: 'PENDING',

                                    requiresConfirmation: newSubscription?.requiresConfirmation ?? false,
                                    requiresClinicianApproval: newSubscription?.requiresClinicianApproval ?? false,
                                    requiresClinicianEncounter: newSubscription?.requiresClinicianEncounter ?? false,
                                    requiresSurveys: newSubscription?.requiresSurveys ?? false,

                                    requiredDocuments: newSubscription?.requiredDocuments ?? [],

                                    source: isStringNotEmpty(newSubscription.source) ? newSubscription.source : null,
                                    affiliateId: isStringNotEmpty(newSubscription.affiliateId) ? newSubscription.affiliateId : null,
                                    subAffiliateId: isStringNotEmpty(newSubscription.subAffiliateId) ? newSubscription.subAffiliateId : null,
                                    campaignId: isStringNotEmpty(newSubscription.campaignId) ? newSubscription.campaignId : null,
                                    programId: isStringNotEmpty(newSubscription.programId) ? newSubscription.programId : null,

                                    coveredEntityId: newSubscription?.coveredEntityId ?? null,
                                    leadId: newSubscription?.leadId ?? null,

                                    affiliateInfo: newSubscription?.affiliateInfo,
                                    deviceInfo: newSubscription?.deviceInfo,
                                    emailInfo: newSubscription?.emailInfo,
                                    phoneInfo: newSubscription?.phoneInfo,

                                    upcharge: upcharge,
                                    includesUpcharge: hasUpcharge,

                                    pharmacyId: isStringNotEmpty(newSubscription.pharmacyId) ? newSubscription.pharmacyId : null,

                                    testMode: testMode,

                                    repId: profile?.repId ?? 'NONE'
                                };

                                if ((isBooleanTrue(newSubscription.requiresConfirmation) && isBooleanTrue(isConfirmed)) ||
                                    (isBooleanFalse(newSubscription.requiresConfirmation) && isBooleanTrue(newSubscription.requiresSurveys) && isBooleanTrue(isSurveysCompleted)) ||
                                    (isBooleanFalse(newSubscription.requiresConfirmation) && isBooleanFalse(newSubscription.requiresSurveys))) {
                                    updatedOrderFields.isConfirmed = isConfirmed;
                                    updatedOrderFields.confirmedAt = moment().utc().toDate();
                                    updatedOrderFields.status = 'CONFIRMED';
                                }

                                if (isBooleanTrue(newSubscription.requiresSurveys) && isBooleanTrue(isSurveysCompleted)) {
                                    updatedOrderFields.isSurveysCompleted = isSurveysCompleted;
                                    updatedOrderFields.surveysCompletedAt = moment().utc().toDate();
                                }

                                if (isListNotEmpty(newSubscription.requiredDocuments) && isBooleanTrue(isRequiredDocumentsProvided)) {
                                    updatedOrderFields.isRequiredDocumentsProvided = isRequiredDocumentsProvided;
                                    updatedOrderFields.requiredDocumentsProvidedAt = moment().utc().toDate();
                                }

                                // create order forms to save to order
                                updatedOrderFields.forms = isListNotEmpty(forms) ? forms.map((form) => {
                                    return new OrderForm({ ...form }).toDto();
                                }) : [];
                                updatedOrderFields.formsCount = forms.length;

                                let updatedOrderSuccess = await Order.updateFields(newOrder?.id, updatedOrderFields);
                                if (isBooleanTrue(updatedOrderSuccess)) {
                                    newOrder = { ...newOrder, ...updatedOrderFields };
                                }
                            }

                            if (isObjectNotEmpty(newOrder) && isStringNotEmpty(newOrder.id)) {
                                orders = updateRecord('id', { ...newOrder }, [...orders]);

                                // create prescription requests for the number of orders allowed per encounter based on subscription settings
                                // take answers from survey to determine where customer starts in titration schedule to help determine number of precription requests
                                // create the prescription request(s)
                                const encounterOrderCount = newSubscription?.ordersPerEncounter ?? 1;

                                let prescriptionRequests = [];
                                if (isNotNullOrUndefined(variantObj)) {
                                    // console.log('pharmacyId', newSubscription?.pharmacyId);
                                    let variantPharmacyMapping = isListNotEmpty(variantObj?.supplierMappings) ? variantObj?.supplierMappings.find(i => i.supplierId === newSubscription?.pharmacyId) : null;
                                    // console.log('variantPharmacyMapping', variantPharmacyMapping);
                                    const newPrescriptionRequest = new PrescriptionRequest({
                                        name: productObj.name,
                                        strength: variantObj.strength,
                                        quantity: (variantPharmacyMapping?.quantity ?? 1),
                                        refills: encounterOrderCount - 1,
                                        medRefId: variantPharmacyMapping?.refId ?? null,
                                        productId: productObj.id,
                                        variantId: variantObj.id
                                    }).toDto();

                                    prescriptionRequests.push(newPrescriptionRequest);
                                }

                                // console.log('prescriptionRequests', prescriptionRequests);

                                let existingEncounter = existingEncounters.find(i => i.subscriptionIds.includes(newSubscription.id));
                                let newEncounter = isObjectNotEmpty(existingEncounter) ? { ...existingEncounter } : null;
                                if (isStringNotEmpty(productObj.clinicianEncounterType) && productObj.clinicianEncounterType !== 'NONE') {
                                    let encounterForms = isListNotEmpty(forms) ? forms.map((form) => {
                                        return new EncounterForm({ ...form }).toDto();
                                    }) : [];

                                    if (isObjectEmpty(newEncounter)) {
                                        let newEncounterFields = {
                                            encounterType: productObj.clinicianEncounterType,
                                            visitType: 'CONSULT',

                                            userId: userId,
                                            customer: new Customer({ ...profile }).toDto(),

                                            practiceId: newSubscription?.coveredEntityId,

                                            status: 'CREATED',

                                            customerIPAddress: newSubscription?.customerIPAddress,
                                            customerUserAgent: newSubscription?.customerUserAgent,
                                            deviceInfo: newSubscription?.deviceInfo,

                                            orderIds: [newOrder.id],
                                            subscriptionIds: [newSubscription.id],
                                            programId: newSubscription.programId,

                                            forms: encounterForms,
                                            formsCount: encounterForms.length,

                                            messageName: program?.name + ' Consultation for ' + (isStringNotEmpty(variantObj?.name) ? variantObj?.name : productObj.name),

                                            prescriptionRequests: prescriptionRequests,

                                            testMode: testMode,

                                            refId: uuid()
                                        };

                                        newEncounter = await new Encounter(newEncounterFields).create();
                                    } else {
                                        let updatedEncounterFields = {
                                            forms: encounterForms,
                                            formsCount: encounterForms.length,

                                            prescriptionRequests: prescriptionRequests,

                                            testMode: testMode,

                                            refId: uuid()
                                        };

                                        let updatedEncounterSuccess = await Encounter.updateFields(newEncounter.id, updatedEncounterFields);
                                        if (isBooleanTrue(updatedEncounterSuccess)) {
                                            newEncounter = { ...newEncounter, ...updatedEncounterFields };
                                        }
                                    }

                                    if (isObjectNotEmpty(newEncounter) && isStringNotEmpty(newEncounter.id)) {
                                        encounters = updateRecord('id', { ...newEncounter }, [...encounters]);

                                        // update subscription and order

                                        // update subscription
                                        let oldRelatedOrderIds = isListNotEmpty(newSubscription.relatedOrderIds) ? [...newSubscription.relatedOrderIds] : [];
                                        let updatedRelatedOrderIds = [...oldRelatedOrderIds, newOrder.id];
                                        let updatedSubscriptionFields = {
                                            relatedOrderIds: updatedRelatedOrderIds,
                                            status: 'PENDING',
                                            statusHistory: StatusHistory.addNewStatus('PENDING', newSubscription.statusHistory),
                                        };
                                        // don't set payment dates on first order since the transaction is a authorization. 
                                        // Payment dates will be set once the authorization is captured
                                        let updatedSubscriptionSuccess2 = await Subscription.updateFields(newSubscription.id, updatedSubscriptionFields);
                                        if (isBooleanTrue(updatedSubscriptionSuccess2)) {
                                            newSubscription = { ...newSubscription, ...updatedSubscriptionFields };
                                            subscriptions = updateRecord('id', { ...newSubscription }, [...subscriptions]);
                                        }

                                        let updatedOrderSuccess2 = await Order.updateField(newOrder?.id, 'encounterId', newEncounter.id);
                                        if (isBooleanTrue(updatedOrderSuccess2)) {
                                            // console.log('updatedOrderSuccess2', updatedOrderSuccess2);
                                            newOrder = { ...newOrder, encounterId: newEncounter?.id };
                                            orders = updateRecord('id', { ...newOrder }, [...orders]);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // update lead
                let updatedLead = isObjectNotEmpty(existingLead) ? { ...existingLead } : null;
                if (isObjectNotEmpty(updatedLead)) {
                    let updatedLeadFields = {
                        subscriptionIds: subscriptions.map(i => i.id),
                        orderIds: orders.map(i => i.id),
                        encounterIds: encounters.map(i => i.id),
                        status: 'PENDING',
                        statusHistory: StatusHistory.addNewStatus('PENDING', updatedLead.statusHistory),
                        acceptConsentAgreements: true,
                        shippingState: shippingAddress?.state,
                        shippingAddress: shippingAddress,
                        billingAddress: billingAddress,

                        testMode: testMode
                    };
                    let updatedLeadSuccess2 = await Lead.updateFields(updatedLead.id, updatedLeadFields);
                    if (isBooleanTrue(updatedLeadSuccess2)) {
                        // console.log('updatedLeadSuccess2', updatedLeadSuccess2);
                        updatedLead = { ...updatedLead, ...updatedLeadFields };
                    }
                }

                return { orders: [...orders], subscriptions: [...subscriptions], encounters: [...encounters], lead: { ...updatedLead } };

            } catch (error) {
                console.log(error);
                throw new Error(error.message);
            }
        }

        return { orders: [], subscriptions: [], encounters: [], lead: null };
    }

    static startSubscription = async (lead, userId, insurances, programId, coveredEntityId) => {
        if (isObjectNotEmpty(lead) && isStringNotEmpty(userId) && isListNotEmpty(insurances) && isStringNotEmpty(programId) && isStringNotEmpty(coveredEntityId)) {
            // get program from database
            const programMatches = await Program.getByField('id', programId, '==');
            if (isListNotEmpty(programMatches)) {
                const programObj = programMatches[0];
                const program = new Program(programObj);

                const campaignId = lead?.campaignId ?? null;

                // TODO: fix
                let product = null;

                let customer = new Customer({
                    firstName: lead.firstName,
                    lastName: lead.lastName,
                    middleName: isStringNotEmpty(lead.middleName) ? lead.middleName : null,
                    suffix: isStringNotEmpty(lead.suffix) ? lead.suffix : null,

                    // write-strict, matching the backend's four Customer sites: an UNKNOWN reaching a
                    // pharmacy payload is stated to a prescriber as sex
                    gender: toWritableGender(lead.gender),
                    dateOfBirth: lead.dateOfBirth,

                    email: toLower(lead.email),
                    phone: toE164PhoneNumber(lead.phone),

                    socialSecurityNumber: lead.socialSecurityNumber
                }).toDto();

                let source = isStringNotEmpty(lead.leadSource) ? lead.leadSource : null;
                let affiliateId = isStringNotEmpty(lead.affiliateId) ? lead.affiliateId : null;
                let subAffiliateId = isStringNotEmpty(lead.subAffiliateId) ? lead.subAffiliateId : null;

                let shippingAddress = isObjectNotEmpty(lead.shippingAddress) ? lead.shippingAddress : null;
                let billingAddress = isObjectNotEmpty(lead.billingAddress) ? lead.billingAddress : null;
                let state = shippingAddress.state;
                let postalCode = shippingAddress.postalCode;

                let primaryInsurance = insurances.find(i => i.rank === 'PRIMARY');
                if (isObjectEmpty(primaryInsurance)) {
                    primaryInsurance = insurances.find(i => i.rank === 'SECONDARY');
                }
                if (isObjectEmpty(primaryInsurance)) {
                    primaryInsurance = insurances.find(i => i.rank === 'TERTIARY');
                }
                if (isObjectEmpty(primaryInsurance)) {
                    primaryInsurance = insurances.find(i => i.planType === 'COMMERCIAL' && i.status === 'ACTIVE');
                }
                if (isObjectEmpty(primaryInsurance)) {
                    primaryInsurance = insurances.find(i => i.planType === 'COMMERCIAL' && i.status === 'CONTACT_PAYER');
                }
                if (isObjectEmpty(primaryInsurance)) {
                    primaryInsurance = insurances.find(i => i.rank === 'OTHER' && i.status === 'ACTIVE');
                }
                if (isObjectEmpty(primaryInsurance)) {
                    primaryInsurance = insurances.find(i => i.rank === 'OTHER' && i.status === 'CONTACT_PAYER');
                }

                let defaultPaymentDetails = new PaymentDetails(null).toDto();
                if (isObjectNotEmpty(primaryInsurance) && isStringNotEmpty(primaryInsurance.payerName) && isStringNotEmpty(primaryInsurance.subscriberId)) {
                    defaultPaymentDetails = new PaymentDetails({
                        method: 'PRIMARY_INSURANCE',

                        payerName: primaryInsurance.payerName,
                        payerCode: primaryInsurance.payerCode,
                        planName: primaryInsurance.planName,
                        planNumber: isStringNotEmpty(primaryInsurance.planNumber) ? primaryInsurance.planNumber : null,
                        groupName: isStringNotEmpty(primaryInsurance.groupName) ? primaryInsurance.groupName : null,
                        groupNumber: isStringNotEmpty(primaryInsurance.groupNumber) ? primaryInsurance.groupNumber : null,
                        planType: primaryInsurance.planType,
                        policyType: isStringNotEmpty(primaryInsurance.policyType) ? primaryInsurance.policyType : null,
                        subscriberId: primaryInsurance.subscriberId,

                        rxBin: isStringNotEmpty(primaryInsurance.rxBin) ? primaryInsurance.rxBin : null,
                        rxPcn: isStringNotEmpty(primaryInsurance.rxPcn) ? primaryInsurance.rxPcn : null,
                        rxGroup: isStringNotEmpty(primaryInsurance.rxGroup) ? primaryInsurance.rxGroup : null,

                        patientFirstName: primaryInsurance.firstName,
                        patientLastName: primaryInsurance.lastName,

                        subscriberFirstName: primaryInsurance.subscriberFirstName,
                        subscriberLastName: primaryInsurance.subscriberLastName,
                        relationshipToSubscriber: primaryInsurance.relationshipToSubscriber,

                        expirationDate: isNotNullOrUndefined(primaryInsurance.effectiveToDate) ? primaryInsurance.effectiveToDate : null,
                        state: isObjectNotEmpty(primaryInsurance.patientAddress) && isStringNotEmpty(primaryInsurance.patientAddress.state) ? primaryInsurance.patientAddress.state : state,
                        postalCode: isObjectNotEmpty(primaryInsurance.patientAddress) && isStringNotEmpty(primaryInsurance.patientAddress.postalCode) ? primaryInsurance.patientAddress.postalCode : postalCode,
                    }).toDto();
                }

                let optedInDate = fromUtcToStartDate(lead.createdAt, 'US/Pacific');
                let importedDate = fromUtcToStartDate(moment(), 'US/Pacific');
                let startDate = importedDate; // TODO: change back to imported date

                let endDate = moment('09/30/2024').set({ hour: 12, minute: 0 }).tz('US/Pacific').startOf('day').utc().toDate();

                // TODO: figure out how to calculate tax
                let lineItems = [];
                let quantity = 1;
                lineItems.push(new OrderLineItem({
                    id: uuid(),
                    name: product.name,
                    sku: product.sku,
                    productId: null, // TODO: replace
                    variantId: null, // TODO: replace
                    quantity: quantity,
                    price: product.getPrice()
                }).toDto());

                let paymentMethods = [];
                if (isObjectNotEmpty(defaultPaymentDetails)) {
                    paymentMethods.push(defaultPaymentDetails);
                }

                let cartTotal = lineItems.map(i => i.price * i.quantity).reduce((partialSum, a) => partialSum + a, 0);
                let cartTax = 0; // lineItems.map(i => i.tax).reduce((partialSum, a) => partialSum + a, 0);
                let subtotal = cartTotal;
                let tax = cartTax;
                let total = subtotal + tax;

                if (isListNotEmpty(paymentMethods)) {
                    try {
                        const newSubscription = await new Subscription({
                            status: 'ACTIVE',
                            userId: userId,
                            coveredEntityId: coveredEntityId,
                            customer: customer,
                            userRefId: isStringNotEmpty(lead.userRefId) ? lead.userRefId : null,

                            billingAddress: billingAddress,
                            shippingAddress: shippingAddress,

                            paymentMethods: paymentMethods,

                            customerIPAddress: lead.ipAddress,
                            customerUserAgent: lead.userAgent,

                            customerNote: null,

                            cartTotal: cartTotal,
                            cartTax: cartTax,

                            subtotal: subtotal,
                            tax: tax,
                            total: total,

                            lineItems: lineItems,

                            startDate: optedInDate,
                            nextPaymentDate: startDate,
                            lastPaymentDate: null,
                            endDate: endDate,

                            billingPeriod: product.subscriptionPeriod ? product.subscriptionPeriod : "MONTH",
                            billingInterval: product.subscriptionPeriodInterval ? product.subscriptionPeriodInterval : 1,
                            billingCycles: product.subscriptionLength ? product.subscriptionLength : 0,

                            source: source,
                            affiliateId: affiliateId,
                            subAffiliateId: subAffiliateId,
                            campaignId: campaignId,
                            programId, programId,

                            testMode: false
                        }).create();

                        if (isObjectNotEmpty(newSubscription) && isStringNotEmpty(newSubscription.id)) {
                            try {
                                await UserNote.create(userId, {
                                    body: `Enrolled in Monthly Subscription through the lead form ${source} and subAffiliateId ${subAffiliateId} and affiliateId ${affiliateId} on ${fromUtcToTimeZone(lead.createdAt, 'US/Pacific')}`,
                                    at: lead.createdAt,
                                    by: userId
                                });
                            } catch (error) {
                                console.log(error);
                            }

                            try {
                                // send out imported welcome email
                                let emailSuccess = await EmailTemplate.sendEmailToUserId('LEAD_IMPORTED', userId, coveredEntityId);
                                if (isBooleanTrue(emailSuccess)) {
                                    await Subscription.updateFields(newSubscription.id, {
                                        welcomeEmailSent: true
                                    });
                                }
                            } catch (error) {
                                console.log(error);
                            }

                            let firstOrder = await Subscription.createFirstOrder(newSubscription, lead);
                            if (isObjectNotEmpty(firstOrder) && isStringNotEmpty(firstOrder.id)) {
                                // console.log('created first order');
                                // let now = new Date();
                                // console.log(fromUtcToStartDate(firstOrder.orderedAt, "US/Pacific").getMonth())
                                // console.log(now.getMonth())
                                // if (fromUtcToStartDate(firstOrder.orderedAt, "US/Pacific").getMonth() < now.getMonth()) {
                                //     console.log(firstOrder.subscriptionId)
                                //     let subscription = await this.getById(firstOrder.subscriptionId);
                                //     let renewalOrder = await this.createRenewalOrder(subscription);
                                //     if (isObjectNotEmpty(renewalOrder) && isStringNotEmpty(renewalOrder.id)) {
                                //         console.log('created renewal order');
                                //         if (fromUtcToStartDate(renewalOrder.orderedAt, "US/Pacific").getMonth() < now.getMonth()) {
                                //             let subscription2 = await this.getById(renewalOrder.subscriptionId);
                                //             let renewalOrder2 = await this.createRenewalOrder(subscription2);
                                //             if (isObjectNotEmpty(renewalOrder2) && isStringNotEmpty(renewalOrder2.id)) {
                                //                 console.log('created second renewal order');
                                //                 if (fromUtcToStartDate(renewalOrder2.orderedAt, "US/Pacific").getMonth() < now.getMonth()) {
                                //                     let subscription3 = await this.getById(renewalOrder2.subscriptionId);
                                //                     let renewalOrder3 = await this.createRenewalOrder(subscription3);

                                //                 }
                                //             }
                                //         }
                                //     }
                                // }
                                return true;
                            }
                        }
                    } catch (error) {
                        console.log(error);
                        throw new Error(error.message);
                    }
                }
            }
        }

        return false;
    }

    static createFirstOrder = async (subscription, lead) => {
        if (isObjectNotEmpty(lead) && isObjectNotEmpty(subscription) && isStringNotEmpty(subscription.id) && isStringNotEmpty(subscription.coveredEntityId) && isStringNotEmpty(subscription.userId) && isObjectNotEmpty(subscription.customer)) {
            let billingPeriod = isStringNotEmpty(subscription.billingPeriod) && ['YEAR', 'MONTH', 'WEEK', 'DAY'].includes(subscription.billingPeriod) ? subscription.billingPeriod : 'MONTH';
            let billingInterval = isNumberGreaterThanZero(subscription.billingInterval) ? Number(subscription.billingInterval) : 1;
            let billingCycles = isNumberNotEmpty(subscription.billingCycles) ? Number(subscription.billingCycles) : 0;

            let orderedAt = isNotNullOrUndefined(subscription.nextPaymentDate) ? fromUtcToStartDate(subscription.nextPaymentDate, 'US/Pacific') : fromUtcToStartDate(moment.tz('US/Pacific'), 'US/Pacific');

            let endDate = fromUtcToStartDate(subscription.endDate, 'US/Pacific');

            // medicaid billing is every 30 days, not every calendar month. therefore, 
            // add 1 month for each renewal. moment's add 1 month will automatically 
            // change the day of the month to be the last day of the next month if the 
            // day of the month in this month is greater than the number of days in the 
            // final month. Therefore, the renewal day will likely stay at the 30th for a 
            // while even if the first order is on the 31st and then will switch to the 28th once February rolls around.
            // let firstDayOfNextMonth = moment(orderedAt).tz('US/Pacific').add(1, 'months').startOf('month').startOf('day').utc().toDate();

            // let oldRelatedOrderIds = isListNotEmpty(subscription.relatedOrderIds) ? [...subscription.relatedOrderIds] : [];

            const existingSubscriptionOrders = await Order.getBySubscriptionId(subscription.id);
            // additional logic in case next month is after end date or if billing cycles isn't infinite and the customer has already maxed out on the billing cycles
            if ((billingCycles === 0 || billingCycles > existingSubscriptionOrders.length) && (orderedAt <= endDate || endDate === null)) {
                // default to 1st day of following calendar month
                //let nextPaymentDate = firstDayOfNextMonth;

                // default to once a month approx 30 days out
                let nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(1, 'months').startOf('day').utc().toDate();
                if (billingPeriod === 'YEAR') {
                    nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(billingInterval, 'years').startOf('day').utc().toDate();
                } else if (billingPeriod === 'MONTH') {
                    nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(billingInterval, 'months').startOf('day').utc().toDate();
                } else if (billingPeriod === 'WEEK') {
                    nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(billingInterval, 'weeks').startOf('day').utc().toDate();
                } else if (billingPeriod === 'DAY') {
                    nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(billingInterval, 'days').startOf('day').utc().toDate();
                }

                let shipments = [];
                // shipments.push(new OrderShipment({
                //     status: 'PENDING',
                //     statusHistory: StatusHistory.addNewStatus('PENDING', [])
                // }).toDto());

                let paymentMethods = isListNotEmpty(subscription.paymentMethods) ? [...subscription.paymentMethods] : [];
                let payments = paymentMethods.map((paymentMethod) => {
                    return new OrderPayment({
                        due: isNumberGreaterThanOrEqualToZero(subscription.total) ? Number(subscription.total) : 0,
                        amount: 0,
                        method: isStringNotEmpty(paymentMethod.method) ? paymentMethod.method : 'OTHER',
                        details: paymentMethod,
                        status: 'PENDING',
                        statusHistory: StatusHistory.addNewStatus('PENDING', [])
                    }).toDto();
                });

                let customerIPAddress = isStringNotEmpty(subscription.customerIPAddress) ? subscription.customerIPAddress : null;
                if (isStringEmpty(customerIPAddress)) {
                    customerIPAddress = await getClientIPAddress();
                }
                const customerUserAgent = isStringNotEmpty(subscription.customerUserAgent) ? subscription.customerUserAgent : getClientUserAgent();
                const customerReferrerUrl = getReferrerUrl();

                let firstOrder = await new Order({
                    orderedAt: orderedAt,
                    subscriptionId: subscription.id,
                    orderType: 'PARENT',

                    status: 'DRAFT',

                    userRefId: isStringNotEmpty(subscription.userRefId) ? subscription.userRefId : null,
                    userId: subscription.userId,
                    customer: subscription.customer,

                    billingStatus: 'PENDING',
                    bypassBillingStatus: false,

                    billingAddress: isObjectNotEmpty(subscription.billingAddress) ? subscription.billingAddress : null,
                    shippingAddress: isObjectNotEmpty(subscription.shippingAddress) ? subscription.shippingAddress : null,

                    customerIPAddress: customerIPAddress,
                    customerUserAgent: customerUserAgent,
                    customerReferrerUrl: customerReferrerUrl,

                    customerNote: isStringNotEmpty(subscription.customerNote) ? subscription.customerNote : null,

                    discountTotal: subscription?.discountTotal ?? 0,
                    discountTax: subscription?.discountTax ?? 0,

                    shippingTotal: subscription?.shippingTotal ?? 0,
                    shippingTax: subscription?.shippingTax ?? 0,

                    feeTotal: subscription?.feeTotal ?? 0,
                    feeTax: subscription?.feeTax ?? 0,

                    cartTotal: subscription?.cartTotal ?? 0,
                    cartTax: subscription?.cartTax ?? 0,

                    subtotal: subscription?.subtotal ?? 0,
                    tax: subscription?.tax ?? 0,
                    total: subscription?.total ?? 0,

                    lineItems: isListNotEmpty(subscription.lineItems) ? subscription.lineItems : [],
                    payments: payments,
                    shipments: shipments,

                    shipmentStatus: 'PENDING',

                    source: isStringNotEmpty(subscription.source) ? subscription.source : null,
                    affiliateId: isStringNotEmpty(subscription.affiliateId) ? subscription.affiliateId : null,
                    subAffiliateId: isStringNotEmpty(subscription.subAffiliateId) ? subscription.subAffiliateId : null,
                    campaignId: isStringNotEmpty(subscription.campaignId) ? subscription.campaignId : null,
                    programId: isStringNotEmpty(subscription.programId) ? subscription.programId : null,

                    coveredEntityId: subscription?.coveredEntityId ?? null,
                    leadId: subscription?.leadId ?? null,

                    pharmacyId: isStringNotEmpty(subscription.pharmacyId) ? subscription.pharmacyId : null,

                    testMode: isBooleanTrue(subscription.testMode),

                    repId: isStringNotEmpty(subscription.repId) ? subscription.repId : 'NONE'
                }).create();

                if (isObjectNotEmpty(firstOrder) && isStringNotEmpty(firstOrder.id)) {
                    let oldRelatedOrderIds = isListNotEmpty(subscription.relatedOrderIds) ? [...subscription.relatedOrderIds] : [];
                    let updatedRelatedOrderIds = [...oldRelatedOrderIds, firstOrder.id];
                    let success = await Subscription.updateFields(subscription.id, {
                        relatedOrderIds: updatedRelatedOrderIds,
                        nextPaymentDate: nextPaymentDate,
                        lastPaymentDate: orderedAt
                    });
                    if (isBooleanTrue(success)) {
                        return firstOrder;
                    }
                }
            }
        }

        return null;
    }

    static createRenewalOrder = async (subscription, status = 'DRAFT') => {
        if (isObjectNotEmpty(subscription) && isStringNotEmpty(subscription.id) && isStringNotEmpty(subscription.userId) && isObjectNotEmpty(subscription.customer)) {
            let billingPeriod = isStringNotEmpty(subscription.billingPeriod) && ['YEAR', 'MONTH', 'WEEK', 'DAY'].includes(subscription.billingPeriod) ? subscription.billingPeriod : 'MONTH';
            let billingInterval = isNumberGreaterThanZero(subscription.billingInterval) ? Number(subscription.billingInterval) : 1;
            let billingCycles = isNumberNotEmpty(subscription.billingCycles) ? Number(subscription.billingCycles) : 0;

            let orderedAt = isNotNullOrUndefined(subscription.nextPaymentDate) ? fromUtcToStartDate(subscription.nextPaymentDate, 'US/Pacific') : fromUtcToStartDate(moment.tz('US/Pacific'), 'US/Pacific');

            let endDate = fromUtcToStartDate(subscription.endDate, 'US/Pacific');

            // medicaid billing is every 30 days, not every calendar month. therefore, 
            // add 1 month for each renewal. moment's add 1 month will automatically 
            // change the day of the month to be the last day of the next month if the 
            // day of the month in this month is greater than the number of days in the 
            // final month. Therefore, the renewal day will likely stay at the 30th for a 
            // while even if the first order is on the 31st and then will switch to the 28th once February rolls around.
            // let firstDayOfNextMonth = moment(orderedAt).tz('US/Pacific').add(1, 'months').startOf('month').startOf('day').utc().toDate();

            // let oldRelatedOrderIds = isListNotEmpty(subscription.relatedOrderIds) ? [...subscription.relatedOrderIds] : [];

            const existingSubscriptionOrders = await Order.getBySubscriptionId(subscription.id);
            // additional logic in case next month is after end date or if billing cycles isn't infinite and the customer has already maxed out on the billing cycles
            if ((billingCycles === 0 || billingCycles > existingSubscriptionOrders.length) && (orderedAt <= endDate || endDate === null)) {
                // default to 1st day of following calendar month
                // let nextPaymentDate = firstDayOfNextMonth;

                // default to once a month approx 30 days out
                let nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(1, 'months').startOf('day').utc().toDate();
                if (billingPeriod === 'YEAR') {
                    nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(billingInterval, 'years').startOf('day').utc().toDate();
                } else if (billingPeriod === 'MONTH') {
                    nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(billingInterval, 'months').startOf('day').utc().toDate();
                } else if (billingPeriod === 'WEEK') {
                    nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(billingInterval, 'weeks').startOf('day').utc().toDate();
                } else if (billingPeriod === 'DAY') {
                    nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(billingInterval, 'days').startOf('day').utc().toDate();
                }

                let lineItems = isListNotEmpty(subscription.lineItems) ? subscription.lineItems : [];
                let taxLines = isListNotEmpty(subscription.taxLines) ? subscription.taxLines : [];
                let shippingLines = isListNotEmpty(subscription.shippingLines) ? subscription.shippingLines : [];
                let feeLines = isListNotEmpty(subscription.feeLines) ? subscription.feeLines : [];
                let discountLines = [];
                if (isListNotEmpty(subscription.discountLines)) {
                    for (const discountLine of subscription.discountLines) {
                        let subscriptionCoupon = await Coupon.getByCode(discountLine.code);
                        if (isObjectNotEmpty(subscriptionCoupon)) {
                            let subscriptionDiscountRes = Order.getOrderDiscount(subscriptionCoupon, [...lineItems], 0, subscription.bundlePaymentCount, subscription.userId, subscription?.customer?.email, null, false);
                            let subscriptionDiscountTotal = subscriptionDiscountRes?.discountTotal ?? 0;
                            let subscriptionDiscountTax = subscriptionDiscountRes?.discountTax ?? 0;
                            // the order is one unit, so it carries one month of the coupon
                            discountLines.push(new OrderDiscountLine({
                                code: subscriptionCoupon?.code,
                                discount: subscriptionDiscountTotal,
                                discountTax: subscriptionDiscountTax,
                                discountTotal: subscriptionDiscountTotal + subscriptionDiscountTax,
                                recurring: isBooleanTrue(['RECURRING', 'BUNDLE', 'MIXED_RECURRING'].includes(subscriptionCoupon?.discountTarget))
                            }).toDto());
                        }
                    }
                }

                let shipments = [];
                // shipments.push(new OrderShipment({
                //     status: 'PENDING',
                //     statusHistory: StatusHistory.addNewStatus('PENDING', [])
                // }).toDto());

                let paymentMethods = isListNotEmpty(subscription.paymentMethods) ? [...subscription.paymentMethods] : [];
                let payments = paymentMethods.map((paymentMethod) => {
                    return new OrderPayment({
                        due: isNumberGreaterThanOrEqualToZero(subscription.total) ? Number(subscription.total) : 0,
                        amount: 0,
                        method: isStringNotEmpty(paymentMethod.method) ? paymentMethod.method : 'OTHER',
                        details: paymentMethod,
                        status: 'PENDING',
                        statusHistory: StatusHistory.addNewStatus('PENDING', [])
                    }).toDto();
                });

                let customerIPAddress = isStringNotEmpty(subscription.customerIPAddress) ? subscription.customerIPAddress : null;
                if (isStringEmpty(customerIPAddress)) {
                    customerIPAddress = await getClientIPAddress();
                }
                const customerUserAgent = isStringNotEmpty(subscription.customerUserAgent) ? subscription.customerUserAgent : getClientUserAgent();
                const customerReferrerUrl = getReferrerUrl();

                let newOrderFields = {
                    orderedAt: orderedAt,
                    subscriptionId: subscription.id,
                    orderType: 'RENEWAL',

                    status: status,

                    userRefId: subscription.userRefId,
                    userId: subscription.userId,
                    customer: subscription.customer,

                    billingStatus: 'PENDING',
                    bypassBillingStatus: subscription.bypassBillingStatus,

                    billingAddress: isObjectNotEmpty(subscription.billingAddress) ? subscription.billingAddress : null,
                    shippingAddress: isObjectNotEmpty(subscription.shippingAddress) ? subscription.shippingAddress : null,

                    customerIPAddress: customerIPAddress,
                    customerUserAgent: customerUserAgent,
                    customerReferrerUrl: customerReferrerUrl,

                    customerNote: isStringNotEmpty(subscription.customerNote) ? subscription.customerNote : null,

                    discountTotal: subscription?.discountTotal ?? 0,
                    discountTax: subscription?.discountTax ?? 0,

                    shippingTotal: subscription?.shippingTotal ?? 0,
                    shippingTax: subscription?.shippingTax ?? 0,

                    feeTotal: subscription?.feeTotal ?? 0,
                    feeTax: subscription?.feeTax ?? 0,

                    cartTotal: subscription?.cartTotal ?? 0,
                    cartTax: subscription?.cartTax ?? 0,

                    subtotal: subscription?.subtotal ?? 0,
                    tax: subscription?.tax ?? 0,
                    total: subscription?.total ?? 0,

                    lineItems: lineItems,
                    taxLines: taxLines,
                    shippingLines: shippingLines,
                    feeLines: feeLines,
                    discountLines: discountLines,

                    payments: payments,
                    shipments: shipments,

                    shipmentStatus: 'PENDING',

                    requiresConfirmation: subscription?.requiresConfirmation ?? false,
                    requiresClinicianApproval: subscription?.requiresClinicianApproval ?? false,
                    requiresClinicianEncounter: subscription?.requiresClinicianEncounter ?? false,
                    requiresSurveys: subscription?.requiresSurveys ?? false,

                    requiredDocuments: subscription?.requiredDocuments ?? [],

                    source: isStringNotEmpty(subscription.source) ? subscription.source : null,
                    affiliateId: isStringNotEmpty(subscription.affiliateId) ? subscription.affiliateId : null,
                    subAffiliateId: isStringNotEmpty(subscription.subAffiliateId) ? subscription.subAffiliateId : null,
                    campaignId: isStringNotEmpty(subscription.campaignId) ? subscription.campaignId : null,
                    programId: isStringNotEmpty(subscription.programId) ? subscription.programId : null,

                    coveredEntityId: subscription?.coveredEntityId ?? null,
                    leadId: subscription?.leadId ?? null,

                    pharmacyId: isStringNotEmpty(subscription.pharmacyId) ? subscription.pharmacyId : null,

                    testMode: isBooleanTrue(subscription.testMode),

                    repId: isStringNotEmpty(subscription.repId) ? subscription.repId : 'NONE'
                };

                if (isBooleanTrue(subscription.requiresConfirmation) && status === 'CONFIRMED') {
                    newOrderFields.isConfirmed = true;
                    newOrderFields.confirmedAt = moment().utc().toDate();
                }

                let renewalOrder = await new Order(newOrderFields).create();

                if (isObjectNotEmpty(renewalOrder) && isStringNotEmpty(renewalOrder.id)) {
                    let oldRelatedOrderIds = isListNotEmpty(subscription.relatedOrderIds) ? [...subscription.relatedOrderIds] : [];
                    let updatedRelatedOrderIds = [...oldRelatedOrderIds, renewalOrder.id];
                    let success = await Subscription.updateFields(subscription.id, {
                        relatedOrderIds: updatedRelatedOrderIds,
                        nextPaymentDate: nextPaymentDate,
                        lastPaymentDate: orderedAt
                    });
                    if (isBooleanTrue(success)) {
                        return { order: renewalOrder, subscription: { ...subscription, relatedOrderIds: updatedRelatedOrderIds, nextPaymentDate: nextPaymentDate, lastPaymentDate: orderedAt } };
                    }
                }
            }
        }

        return { order: null, subscription: null };
    }

    static createVoucherOrder = async (subscription, product, taxRate, forms = [], affiliateInfo = null, deviceInfo = null, emailInfo = null, phoneInfo = null, isConfirmed = false, isSurveysCompleted = false, isRequiredDocumentsProvided = false) => {
        if (isObjectNotEmpty(subscription) && isStringNotEmpty(subscription.id) && isStringNotEmpty(subscription.userId) && isObjectNotEmpty(subscription.customer)) {
            let billingPeriod = isStringNotEmpty(subscription.billingPeriod) && ['YEAR', 'MONTH', 'WEEK', 'DAY'].includes(subscription.billingPeriod) ? subscription.billingPeriod : 'MONTH';
            let billingInterval = isNumberGreaterThanZero(subscription.billingInterval) ? Number(subscription.billingInterval) : 1;
            let billingCycles = isNumberNotEmpty(subscription.billingCycles) ? Number(subscription.billingCycles) : 0;

            let orderedAt = isNotNullOrUndefined(subscription.nextPaymentDate) ? fromUtcToStartDate(subscription.nextPaymentDate, 'US/Pacific') : fromUtcToStartDate(moment.tz('US/Pacific'), 'US/Pacific');

            let endDate = fromUtcToStartDate(subscription.endDate, 'US/Pacific');

            // medicaid billing is every 30 days, not every calendar month. therefore, 
            // add 1 month for each renewal. moment's add 1 month will automatically 
            // change the day of the month to be the last day of the next month if the 
            // day of the month in this month is greater than the number of days in the 
            // final month. Therefore, the renewal day will likely stay at the 30th for a 
            // while even if the first order is on the 31st and then will switch to the 28th once February rolls around.
            // let firstDayOfNextMonth = moment(orderedAt).tz('US/Pacific').add(1, 'months').startOf('month').startOf('day').utc().toDate();

            // let oldRelatedOrderIds = isListNotEmpty(subscription.relatedOrderIds) ? [...subscription.relatedOrderIds] : [];

            const frequency = Number(subscription.bundlePaymentCount) ?? 1;

            const existingSubscriptionOrders = await Order.getBySubscriptionId(subscription.id);
            // additional logic in case next month is after end date or if billing cycles isn't infinite and the customer has already maxed out on the billing cycles
            if ((billingCycles === 0 || billingCycles > existingSubscriptionOrders.length) && (orderedAt <= endDate || endDate === null)) {
                // default to 1st day of following calendar month
                // let nextPaymentDate = firstDayOfNextMonth;

                // default to once a month approx 30 days out
                let nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(1 * frequency, 'months').startOf('day').utc().toDate();
                if (billingPeriod === 'YEAR') {
                    nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(billingInterval * frequency, 'years').startOf('day').utc().toDate();
                } else if (billingPeriod === 'MONTH') {
                    nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(billingInterval * frequency, 'months').startOf('day').utc().toDate();
                } else if (billingPeriod === 'WEEK') {
                    nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(billingInterval * frequency, 'weeks').startOf('day').utc().toDate();
                } else if (billingPeriod === 'DAY') {
                    nextPaymentDate = moment(orderedAt).tz('US/Pacific').add(billingInterval * frequency, 'days').startOf('day').utc().toDate();
                }

                let shipments = [];
                // shipments.push(new OrderShipment({
                //     status: 'PENDING',
                //     statusHistory: StatusHistory.addNewStatus('PENDING', [])
                // }).toDto());

                let oldLineItems = isListNotEmpty(subscription.lineItems) ? subscription.lineItems : [];
                let lineItems = [];
                if (isListNotEmpty(oldLineItems)) {
                    for (const oldLineItem of oldLineItems) {
                        let quantity = subscription.bundlePaymentCount > 1 ? subscription.bundlePaymentCount : 1;
                        let oldPrice = oldLineItem.price;
                        let price = oldPrice;
                        let lineItemProduct = await Product.getById(oldLineItem?.productId);
                        if (isObjectNotEmpty(lineItemProduct)) {
                            // if price of product has changed and it is less than the current price on the subscription, 
                            // change the price to the new lower price when the customer renews
                            let lineItemProductObj = new Product(lineItemProduct);
                            let newPrice = lineItemProductObj.getPrice();
                            if (newPrice <= oldPrice) {
                                price = newPrice;
                            }
                        }
                        let subtotal = quantity * price;
                        let tax = quantity * oldLineItem.tax;
                        let total = subtotal + tax;
                        lineItems.push(new OrderLineItem({
                            ...oldLineItem,
                            price: price,
                            quantity: quantity,
                            subtotal: subtotal,
                            tax: tax,
                            total: total
                        }).toDto());
                    }
                }

                let taxLines = isListNotEmpty(subscription.taxLines) ? subscription.taxLines : [];
                let shippingLines = isListNotEmpty(subscription.shippingLines) ? subscription.shippingLines : [];
                let feeLines = isListNotEmpty(subscription.feeLines) ? subscription.feeLines : [];
                let discountLines = [];
                if (isListNotEmpty(subscription.discountLines)) {
                    for (const discountLine of subscription.discountLines) {
                        let subscriptionCoupon = await Coupon.getByCode(discountLine.code);
                        if (isObjectNotEmpty(subscriptionCoupon)) {
                            let subscriptionDiscountRes = Order.getOrderDiscount(subscriptionCoupon, [...lineItems], taxRate, subscription.bundlePaymentCount, subscription.userId, subscription?.customer?.email, null, false);
                            let subscriptionDiscountTotal = subscriptionDiscountRes?.discountTotal ?? 0;
                            let subscriptionDiscountTax = subscriptionDiscountRes?.discountTax ?? 0;
                            let applyToBundle = isNumberGreaterThanOrEqualToZero(subscription.bundlePaymentCount) && subscription.bundlePaymentCount > 1 && isBooleanTrue(subscriptionCoupon?.applyToBundle) && isBooleanTrue(['RECURRING', 'BUNDLE', 'MIXED_RECURRING'].includes(subscriptionCoupon?.discountTarget));
                            let applyToBundleCount = isBooleanTrue(applyToBundle) ? subscription.bundlePaymentCount : 1;
                            discountLines.push(new OrderDiscountLine({
                                code: subscriptionCoupon?.code,
                                discount: subscriptionDiscountTotal * applyToBundleCount,
                                discountTax: subscriptionDiscountTax * applyToBundleCount,
                                discountTotal: (subscriptionDiscountTotal + subscriptionDiscountTax) * applyToBundleCount,
                                recurring: isBooleanTrue(['RECURRING', 'BUNDLE', 'MIXED_RECURRING'].includes(subscriptionCoupon?.discountTarget))
                            }).toDto());
                        }
                    }
                }

                if (isNumberGreaterThanOrEqualToZero(subscription.bundlePaymentCount) && subscription.bundlePaymentCount > 1) {
                    let productBundleDiscounts = isListNotEmpty(product.bundleDiscounts) ? [...product.bundleDiscounts] : [];
                    let bundleDiscount = productBundleDiscounts.find(i => i.bundlePaymentCount === subscription.bundlePaymentCount);
                    if (isObjectNotEmpty(bundleDiscount)) {
                        let bundleCoupon = await Coupon.getByCode(bundleDiscount.couponCode);
                        if (isObjectNotEmpty(bundleCoupon)) {
                            let bundleDiscountRes = Order.getOrderDiscount(bundleCoupon, [...lineItems], taxRate, subscription.bundlePaymentCount, subscription.userId, subscription?.customer?.email, null, false);
                            let bundleDiscountTotal = bundleDiscountRes?.discountTotal ?? 0;
                            let bundleDiscountTax = bundleDiscountRes?.discountTax ?? 0;
                            discountLines.push(new OrderDiscountLine({
                                code: bundleCoupon?.code,
                                discount: bundleDiscountTotal,
                                discountTax: bundleDiscountTax,
                                discountTotal: bundleDiscountTotal + bundleDiscountTax,
                                recurring: true
                            }).toDto());

                            lineItems = lineItems.map((lineItem) => {
                                let quantity = subscription.bundlePaymentCount;
                                let subtotal = quantity * lineItem.price;
                                let tax = quantity * lineItem.tax;
                                let total = subtotal + tax;
                                return new OrderLineItem({
                                    ...lineItem,
                                    quantity: subscription.bundlePaymentCount,
                                    subtotal: subtotal,
                                    tax: tax,
                                    total: total
                                }).toDto();
                            });
                        }
                    }
                }

                let orderSummary = Order.calculateOrderSummary(lineItems, discountLines, feeLines, shippingLines);

                let paymentMethods = isListNotEmpty(subscription.paymentMethods) ? [...subscription.paymentMethods] : [];
                let payments = paymentMethods.map((paymentMethod) => {
                    return new OrderPayment({
                        due: isNumberGreaterThanOrEqualToZero(orderSummary.total) ? Number(orderSummary.total) : 0,
                        amount: 0,
                        method: isStringNotEmpty(paymentMethod.method) ? paymentMethod.method : 'OTHER',
                        details: paymentMethod,
                        status: 'PENDING',
                        statusHistory: StatusHistory.addNewStatus('PENDING', [])
                    }).toDto();
                });

                let customerIPAddress = isStringNotEmpty(subscription.customerIPAddress) ? subscription.customerIPAddress : null;
                if (isStringEmpty(customerIPAddress)) {
                    customerIPAddress = await getClientIPAddress();
                }
                const customerUserAgent = isStringNotEmpty(subscription.customerUserAgent) ? subscription.customerUserAgent : getClientUserAgent();
                const customerReferrerUrl = getReferrerUrl();

                let newOrderFields = {
                    orderedAt: orderedAt,
                    subscriptionId: subscription.id,
                    orderType: 'VOUCHER',

                    status: 'DRAFT',

                    userRefId: subscription.userRefId,
                    userId: subscription.userId,
                    customer: subscription.customer,

                    billingStatus: 'PENDING',
                    bypassBillingStatus: subscription.bypassBillingStatus,

                    billingAddress: isObjectNotEmpty(subscription.billingAddress) ? subscription.billingAddress : null,
                    shippingAddress: isObjectNotEmpty(subscription.shippingAddress) ? subscription.shippingAddress : null,

                    customerIPAddress: customerIPAddress,
                    customerUserAgent: customerUserAgent,
                    customerReferrerUrl: customerReferrerUrl,

                    customerNote: isStringNotEmpty(subscription.customerNote) ? subscription.customerNote : null,

                    discountTotal: orderSummary?.discountTotal ?? 0,
                    discountTax: orderSummary?.discountTax ?? 0,

                    shippingTotal: orderSummary?.shippingTotal ?? 0,
                    shippingTax: orderSummary?.shippingTax ?? 0,

                    feeTotal: orderSummary?.feeTotal ?? 0,
                    feeTax: orderSummary?.feeTax ?? 0,

                    cartTotal: orderSummary?.cartTotal ?? 0,
                    cartTax: orderSummary?.cartTax ?? 0,

                    subtotal: orderSummary?.subtotal ?? 0,
                    tax: orderSummary?.tax ?? 0,
                    total: orderSummary?.total ?? 0,

                    lineItems: lineItems,
                    taxLines: taxLines,
                    shippingLines: shippingLines,
                    feeLines: feeLines,
                    discountLines: discountLines,

                    payments: payments,
                    shipments: shipments,

                    shipmentStatus: 'PENDING',

                    requiresConfirmation: subscription?.requiresConfirmation ?? false,
                    requiresClinicianApproval: subscription?.requiresClinicianApproval ?? false,
                    requiresClinicianEncounter: subscription?.requiresClinicianEncounter ?? false,
                    requiresSurveys: subscription?.requiresSurveys ?? false,

                    requiredDocuments: subscription?.requiredDocuments ?? [],

                    source: isStringNotEmpty(subscription.source) ? subscription.source : null,
                    affiliateId: isStringNotEmpty(subscription.affiliateId) ? subscription.affiliateId : null,
                    subAffiliateId: isStringNotEmpty(subscription.subAffiliateId) ? subscription.subAffiliateId : null,
                    campaignId: isStringNotEmpty(subscription.campaignId) ? subscription.campaignId : null,
                    programId: isStringNotEmpty(subscription.programId) ? subscription.programId : null,

                    coveredEntityId: subscription?.coveredEntityId ?? null,
                    leadId: subscription?.leadId ?? null,

                    affiliateInfo: affiliateInfo,
                    deviceInfo: deviceInfo,
                    emailInfo: emailInfo,
                    phoneInfo: phoneInfo,

                    upcharge: subscription?.upcharge ?? 0,
                    includesUpcharge: isBooleanTrue(subscription?.hasUpcharge),

                    pharmacyId: isStringNotEmpty(subscription.pharmacyId) ? subscription.pharmacyId : null,

                    testMode: isBooleanTrue(subscription.testMode),

                    repId: isStringNotEmpty(subscription.repId) ? subscription.repId : 'NONE'
                };

                if ((isBooleanTrue(subscription.requiresConfirmation) && isBooleanTrue(isConfirmed)) ||
                    (isBooleanFalse(subscription.requiresConfirmation) && isBooleanTrue(subscription.requiresSurveys) && isBooleanTrue(isSurveysCompleted)) ||
                    (isBooleanFalse(subscription.requiresConfirmation) && isBooleanFalse(subscription.requiresSurveys))) {
                    newOrderFields.isConfirmed = true;
                    newOrderFields.confirmedAt = moment().utc().toDate();
                    newOrderFields.status = 'CONFIRMED';
                }

                if (isBooleanTrue(subscription.requiresSurveys) && isBooleanTrue(isSurveysCompleted)) {
                    newOrderFields.isSurveysCompleted = true;
                    newOrderFields.surveysCompletedAt = moment().utc().toDate();
                }

                if (isListNotEmpty(subscription.requiredDocuments) && isBooleanTrue(isRequiredDocumentsProvided)) {
                    newOrderFields.isRequiredDocumentsProvided = true;
                    newOrderFields.requiredDocumentsProvidedAt = moment().utc().toDate();
                }

                // create order forms to save to order
                newOrderFields.forms = isListNotEmpty(forms) ? forms.map((form) => {
                    return new OrderForm({ ...form }).toDto();
                }) : [];
                newOrderFields.formsCount = forms.length;

                let voucherOrder = await new Order(newOrderFields).create();
                if (isObjectNotEmpty(voucherOrder) && isStringNotEmpty(voucherOrder.id)) {
                    let oldRelatedOrderIds = isListNotEmpty(subscription.relatedOrderIds) ? [...subscription.relatedOrderIds] : [];
                    let updatedRelatedOrderIds = [...oldRelatedOrderIds, voucherOrder.id];
                    let success = await Subscription.updateFields(subscription.id, {
                        relatedOrderIds: updatedRelatedOrderIds,
                        nextPaymentDate: nextPaymentDate,
                        lastPaymentDate: orderedAt
                    });
                    if (isBooleanTrue(success)) {
                        return { order: voucherOrder, subscription: { ...subscription, relatedOrderIds: updatedRelatedOrderIds, nextPaymentDate: nextPaymentDate, lastPaymentDate: orderedAt } };
                    }
                }
            }
        }

        return { order: null, subscription: null };
    }

    static cancelSubscription = async (id, cancelScheduledEncounters = false) => {
        const subscription = await Subscription.getById(id);
        if (isObjectNotEmpty(subscription)) {
            let cancelledDate = moment.tz('US/Pacific').utc().toDate();

            // cancel all orders that havent been processed yet
            let ordersAreCancelled = true;
            const relatedOrders = await Order.getBySubscriptionId(subscription.id);
            if (isListNotEmpty(relatedOrders)) {
                for (const relatedOrder of relatedOrders) {
                    if (['DRAFT', 'CONFIRMED', 'APPROVED', 'READY', 'PENDING', 'FAILED', 'COLLECTIONS', 'SKIPPED'].includes(relatedOrder.status)) {
                        // allow order to be cancelled
                        let success = await Order.updateFields(relatedOrder.id, {
                            status: 'CANCELLED',
                            statusHistory: StatusHistory.addNewStatus('CANCELLED', relatedOrder.statusHistory, cancelledDate),
                            statusLastUpdatedAt: cancelledDate,
                            statusLastUpdatedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
                        });
                        if (isBooleanFalse(success)) {
                            ordersAreCancelled = false;
                        } else {
                            if (relatedOrder.orderType === 'PRESCRIPTION') {
                                // cancel the prescription as well
                                let prescription = await Prescription.getById(relatedOrder.prescriptionId);
                                if (isObjectNotEmpty(prescription)) {
                                    let updatedPrescriptionFields = {
                                        status: 'CANCELLED',
                                        statusHistory: StatusHistory.addNewStatus('CANCELLED', prescription.statusHistory)
                                    };
                                    await Prescription.updateFields(prescription.id, updatedPrescriptionFields);
                                }
                            }
                        }
                    }
                }
            }

            if (isBooleanTrue(cancelScheduledEncounters)) {
                const relatedEncounters = await Encounter.getBySubscriptionId(subscription.id);
                if (isListNotEmpty(relatedEncounters)) {
                    for (const relatedEncounter of relatedEncounters) {
                        if (['SCHEDULED', 'REQUESTED'].includes(relatedEncounter.status)) {
                            // allow encounter to be cancelled
                            await Encounter.updateFields(relatedEncounter.id, {
                                status: 'CANCELLED',
                                statusHistory: StatusHistory.addNewStatus('CANCELLED', relatedEncounter.statusHistory, cancelledDate),
                                cancelledAt: cancelledDate,
                                isCancelled: true
                            });
                        } else if (['STARTED', 'ASSIGNED'].includes(relatedEncounter.status)) {
                            // Beluga reports the real result inside the body, and `alreadyProcessed` still carries an
                            // authoritative outcome. An empty body means we never reached them, so leave the encounter
                            // as-is rather than recording a closure Beluga does not have.
                            const cancelEncounterResponse = await cancelBelugaVisit(relatedEncounter.refId, isBooleanTrue(relatedEncounter.testMode));
                            const cancelOutcome = isObjectNotEmpty(cancelEncounterResponse) && cancelEncounterResponse.status === 200 ? cancelEncounterResponse.outcome : null;

                            if (cancelOutcome === 'canceled') {
                                await Encounter.updateFields(relatedEncounter.id, {
                                    status: 'CANCELLED',
                                    statusHistory: StatusHistory.addNewStatus('CANCELLED', relatedEncounter.statusHistory, cancelledDate),
                                    cancelledAt: cancelledDate,
                                    isCancelled: true
                                });
                            } else if (cancelOutcome === 'resolved') {
                                // a doctor already prescribed, so Beluga closed the visit out instead of cancelling it
                                await Encounter.updateFields(relatedEncounter.id, {
                                    status: 'COMPLETED',
                                    statusHistory: StatusHistory.addNewStatus('COMPLETED', relatedEncounter.statusHistory, cancelledDate),
                                    completedAt: cancelledDate,
                                    isCompleted: true
                                });
                            } else {
                                // the visit may still be live at Beluga on a subscription we just cancelled, which is
                                // the billing exposure this flow exists to close, so it has to reach a human
                                console.log(`Unable to cancel Beluga visit ${relatedEncounter.refId} for encounter ${relatedEncounter.id}; encounter left unchanged`, cancelEncounterResponse);
                                await sendEmailToIT(
                                    `URGENT: Beluga visit ${relatedEncounter.refId} may still be open after a subscription was cancelled`,
                                    JSON.stringify({
                                        encounterId: relatedEncounter.id,
                                        encounterRefId: relatedEncounter.refId,
                                        encounterStatus: relatedEncounter.status,
                                        subscriptionId: subscription.id,
                                        subscriptionNumber: subscription.subscriptionNumber,
                                        cancelEncounterResponse: cancelEncounterResponse
                                    }, null, 2)
                                );
                            }
                        }
                    }
                }
            }

            // if the orders have been cancelled then cancel the subscription
            if (isBooleanTrue(ordersAreCancelled)) {
                let updatedFields = {
                    status: 'CANCELLED',
                    statusHistory: StatusHistory.addNewStatus('CANCELLED', subscription.statusHistory, cancelledDate),
                    cancelledDate: cancelledDate,
                    cancelledEmailSent: false,
                    nextPaymentDate: null
                };
                // cancel welcome call if subscription is being cancelled before welcome call is completed
                const terminalStatuses = ['COMPLETED', 'DECLINED', 'CANCELLED'];
                if (!terminalStatuses.includes(subscription.welcomeCallStatus)) {
                    updatedFields.welcomeCallStatus = 'CANCELLED';

                    try {
                        // mark welcome call task as cancelled
                        let matches = await Task.getByEntityIdAndEntityType(subscription.id, 'SUBSCRIPTION');
                        if (isListNotEmpty(matches)) {
                            let welcomeCallTask = matches.find(t => t.category === 'WELCOME' && !['COMPLETED', 'CANCELLED'].includes(t.status));
                            if (isObjectNotEmpty(welcomeCallTask)) {
                                let taskUpdateSuccess = await Task.updateFields(welcomeCallTask.id, {
                                    status: 'CANCELLED',
                                    statusHistory: StatusHistory.addNewStatus('CANCELLED', welcomeCallTask.statusHistory),
                                    completedAt: moment().utc().toDate()
                                });
                                if (isBooleanTrue(taskUpdateSuccess)) {
                                    console.log(`Welcome call task ${welcomeCallTask.id} marked as completed`);
                                }
                            }
                        }
                    } catch (err) {
                        console.log(`Error cancelling welcome call task for subscription ${subscription.id}:`, err);
                    }
                }

                let subscriptionSuccess = await Subscription.updateFields(subscription.id, updatedFields);
                if (isBooleanTrue(subscriptionSuccess) && isStringNotEmpty(subscription.userId)) {
                    try {
                        await UserNote.create(subscription.userId, {
                            body: `Cancelled Subscription via Subscription Management on ${fromUtcToTimeZone(cancelledDate, 'US/Pacific')}`,
                            at: cancelledDate,
                            by: subscription.userId
                        });
                    } catch (error) {
                        console.log(error);
                    }

                    try {
                        // send out cancellation email
                        await EmailTemplate.sendSubscriptionEmailToUser('SUBSCRIPTION_CANCELLED', subscription.customer, subscription, subscription.coveredEntityId, null);
                    } catch (error) {
                        console.log(error);
                    }
                }

                return subscriptionSuccess;
            }
        }

        return false;
    }

    static pauseSubscription = async (id) => {
        const subscription = await Subscription.getById(id);
        if (isObjectNotEmpty(subscription)) {
            let onHoldDate = moment.tz('US/Pacific').utc().toDate();

            // cancel or skip all orders that havent been processed yet
            let ordersAreOnHold = true;
            const relatedOrders = await Order.getBySubscriptionId(subscription.id);
            if (isListNotEmpty(relatedOrders)) {
                for (const relatedOrder of relatedOrders) {
                    if (['DRAFT', 'CONFIRMED', 'APPROVED', 'READY', 'PENDING', 'FAILED', 'COLLECTIONS'].includes(relatedOrder.status)) {
                        if (relatedOrder.orderType === 'VOUCHER') {
                            // cancel the voucher orders
                            let success = await Order.updateFields(relatedOrder.id, {
                                status: 'CANCELLED',
                                statusHistory: StatusHistory.addNewStatus('CANCELLED', relatedOrder.statusHistory, onHoldDate),
                                statusLastUpdatedAt: onHoldDate,
                                statusLastUpdatedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
                            });
                            if (isBooleanFalse(success)) {
                                ordersAreOnHold = false;
                            }
                        } else {
                            // skip the regular orders and prescription orders
                            let success = await Order.updateFields(relatedOrder.id, {
                                status: 'SKIPPED',
                                statusHistory: StatusHistory.addNewStatus('SKIPPED', relatedOrder.statusHistory, onHoldDate),
                                statusLastUpdatedAt: onHoldDate,
                                statusLastUpdatedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
                            });
                            if (isBooleanFalse(success)) {
                                ordersAreOnHold = false;
                            }
                        }
                    }
                }
            }

            // if the orders have been skipped then put the subscription on hold
            if (isBooleanTrue(ordersAreOnHold)) {
                let updatedFields = {
                    status: 'ON_HOLD',
                    statusHistory: StatusHistory.addNewStatus('ON_HOLD', subscription.statusHistory, onHoldDate)
                };
                let subscriptionSuccess = await Subscription.updateFields(subscription.id, updatedFields);

                return subscriptionSuccess;
            }
        }

        return false;
    }

    static pauseSubscriptionsByUserId = async (userId) => {
        let onHoldDate = moment.tz('US/Pacific').utc().toDate();
        const subscriptions = await Subscription.getByUserId(userId);
        let subscriptionsAreOnHold = true;
        for (const subscription of subscriptions) {
            // cancel all orders that havent been processed yet
            let ordersAreOnHold = true;
            const relatedOrders = await Order.getBySubscriptionId(subscription.id);
            if (isListNotEmpty(relatedOrders)) {
                for (const relatedOrder of relatedOrders) {
                    if (['DRAFT', 'CONFIRMED', 'APPROVED', 'READY', 'PENDING', 'FAILED', 'COLLECTIONS'].includes(relatedOrder.status)) {
                        if (relatedOrder.orderType === 'VOUCHER') {
                            // cancel the voucher orders
                            let success = await Order.updateFields(relatedOrder.id, {
                                status: 'CANCELLED',
                                statusHistory: StatusHistory.addNewStatus('CANCELLED', relatedOrder.statusHistory, onHoldDate),
                                statusLastUpdatedAt: onHoldDate,
                                statusLastUpdatedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
                            });
                            if (isBooleanFalse(success)) {
                                ordersAreOnHold = false;
                            }
                        } else {
                            // skip the regular orders and prescription orders
                            let success = await Order.updateFields(relatedOrder.id, {
                                status: 'SKIPPED',
                                statusHistory: StatusHistory.addNewStatus('SKIPPED', relatedOrder.statusHistory, onHoldDate),
                                statusLastUpdatedAt: onHoldDate,
                                statusLastUpdatedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
                            });
                            if (isBooleanFalse(success)) {
                                ordersAreOnHold = false;
                            }
                        }
                    }
                }
            }

            // if the orders have been skipped then put the subscription on hold
            if (isBooleanTrue(ordersAreOnHold)) {
                let updatedFields = {
                    status: 'ON_HOLD',
                    statusHistory: StatusHistory.addNewStatus('ON_HOLD', subscription.statusHistory, onHoldDate)
                };
                let subscriptionSuccess = await Subscription.updateFields(subscription.id, updatedFields);
                if (isBooleanFalse(subscriptionSuccess)) {
                    subscriptionsAreOnHold = false;
                }
                //return subscriptionSuccess;
            }
        }

        return subscriptionsAreOnHold;
    }

    static getCount = async (queryParameters, dateFieldName = 'createdAt') => {
        const { state, testMode, userId, repId, programId, status, coveredEntityId, pharmacyId, bundleShipmentCount, bundlePaymentCount, productSku, productId, variantId, affiliateId, start, end } = queryParameters;

        let queryConstraints = [];

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', 'in', coveredEntityId));
        } else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }

        if (isListNotEmpty(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', 'in', pharmacyId));
        } else if (isStringNotEmptyOrZeroLength(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', '==', pharmacyId));
        }

        if (isListNotEmpty(bundlePaymentCount)) {
            queryConstraints.push(where('bundlePaymentCount', 'in', bundlePaymentCount));
        } else if (isNumberNotEmpty(bundlePaymentCount)) {
            queryConstraints.push(where('bundlePaymentCount', '==', bundlePaymentCount));
        }

        if (isListNotEmpty(bundleShipmentCount)) {
            queryConstraints.push(where('bundleShipmentCount', 'in', bundleShipmentCount));
        } else if (isNumberNotEmpty(bundleShipmentCount)) {
            queryConstraints.push(where('bundleShipmentCount', '==', bundleShipmentCount));
        }

        if (isListNotEmpty(state)) {
            queryConstraints.push(where('shippingAddress.state', 'in', state));
        } else if (isStringNotEmptyOrZeroLength(state)) {
            queryConstraints.push(where('shippingAddress.state', '==', state));
        }

        if (isListNotEmpty(productSku)) {
            queryConstraints.push(where('productSku', 'in', productSku));
        } else if (isStringNotEmptyOrZeroLength(productSku)) {
            queryConstraints.push(where('productSku', '==', productSku));
        }

        if (isListNotEmpty(productId)) {
            queryConstraints.push(where('productId', 'in', productId));
        } else if (isStringNotEmptyOrZeroLength(productId)) {
            queryConstraints.push(where('productId', '==', productId));
        }

        if (isListNotEmpty(variantId)) {
            queryConstraints.push(where('variantId', 'in', variantId));
        } else if (isStringNotEmptyOrZeroLength(variantId)) {
            queryConstraints.push(where('variantId', '==', variantId));
        }

        if (isListNotEmpty(affiliateId)) {
            queryConstraints.push(where('affiliateId', 'in', affiliateId));
        } else if (isStringNotEmptyOrZeroLength(affiliateId)) {
            queryConstraints.push(where('affiliateId', '==', affiliateId));
        }

        if (isListNotEmpty(repId)) {
            queryConstraints.push(where('repId', 'in', repId));
        } else if (isStringNotEmptyOrZeroLength(repId)) {
            if (repId === 'NONE') {
                queryConstraints.push(where('repId', '==', 'NONE'));
            } else if (repId === 'ASSIGNED') {
                queryConstraints.push(where('repId', '!=', 'NONE'));
            } else {
                queryConstraints.push(where('repId', '==', repId));
            }
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where(dateFieldName, '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where(dateFieldName, '<=', endDate));
        }

        let q = query(collection(db, Subscription.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt', dateFieldName = 'createdAt') => {
        const { state, testMode, userId, repId, programId, status, coveredEntityId, pharmacyId, bundleShipmentCount, bundlePaymentCount, productSku, productId, variantId, affiliateId, start, end } = queryParameters;

        let queryConstraints = [];

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', 'in', coveredEntityId));
        } else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }

        if (isListNotEmpty(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', 'in', pharmacyId));
        } else if (isStringNotEmptyOrZeroLength(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', '==', pharmacyId));
        }

        if (isListNotEmpty(bundlePaymentCount)) {
            queryConstraints.push(where('bundlePaymentCount', 'in', bundlePaymentCount));
        } else if (isNumberNotEmpty(bundlePaymentCount)) {
            queryConstraints.push(where('bundlePaymentCount', '==', bundlePaymentCount));
        }

        if (isListNotEmpty(bundleShipmentCount)) {
            queryConstraints.push(where('bundleShipmentCount', 'in', bundleShipmentCount));
        } else if (isNumberNotEmpty(bundleShipmentCount)) {
            queryConstraints.push(where('bundleShipmentCount', '==', bundleShipmentCount));
        }

        if (isListNotEmpty(state)) {
            queryConstraints.push(where('shippingAddress.state', 'in', state));
        } else if (isStringNotEmptyOrZeroLength(state)) {
            queryConstraints.push(where('shippingAddress.state', '==', state));
        }

        if (isListNotEmpty(productSku)) {
            queryConstraints.push(where('productSku', 'in', productSku));
        } else if (isStringNotEmptyOrZeroLength(productSku)) {
            queryConstraints.push(where('productSku', '==', productSku));
        }

        if (isListNotEmpty(productId)) {
            queryConstraints.push(where('productId', 'in', productId));
        } else if (isStringNotEmptyOrZeroLength(productId)) {
            queryConstraints.push(where('productId', '==', productId));
        }

        if (isListNotEmpty(variantId)) {
            queryConstraints.push(where('variantId', 'in', variantId));
        } else if (isStringNotEmptyOrZeroLength(variantId)) {
            queryConstraints.push(where('variantId', '==', variantId));
        }

        if (isListNotEmpty(affiliateId)) {
            queryConstraints.push(where('affiliateId', 'in', affiliateId));
        } else if (isStringNotEmptyOrZeroLength(affiliateId)) {
            queryConstraints.push(where('affiliateId', '==', affiliateId));
        }

        if (isListNotEmpty(repId)) {
            queryConstraints.push(where('repId', 'in', repId));
        } else if (isStringNotEmptyOrZeroLength(repId)) {
            if (repId === 'NONE') {
                queryConstraints.push(where('repId', '==', 'NONE'));
            } else if (repId === 'ASSIGNED') {
                queryConstraints.push(where('repId', '!=', 'NONE'));
            } else {
                queryConstraints.push(where('repId', '==', repId));
            }
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where(dateFieldName, '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where(dateFieldName, '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, Subscription.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['repId'], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, Subscription.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['repId'], lastVisible['id']));
        } else {
            q = query(collection(db, Subscription.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters, dateFieldName = 'createdAt') => {
        let allRecords = [];
        try {
            let next = null;

            let totalCount = await Subscription.getCount(queryParameters, dateFieldName);
            let totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await Subscription.getPaginated(queryParameters, 10000, null, next, dateFieldName, dateFieldName);

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }

    static getCountByNextRenewalDate = async (programId, status, start, end) => {
        let queryConstraints = [];

        if (isStringNotEmpty(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmpty(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('nextPaymentDate', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('nextPaymentDate', '<=', endDate));
        }

        let q = query(collection(db, Subscription.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        return snapShot.data().count;
    }

    static getByNextRenewalDate = async (programId, status, start, end) => {
        let allRecords = [];
        try {
            let next = null;
            let totalPages = 0;

            let totalCount = await Subscription.getCountByNextRenewalDate(programId, status, start, end);
            totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let queryConstraints = [];

                if (isStringNotEmptyOrZeroLength(programId)) {
                    queryConstraints.push(where('programId', '==', programId));
                }

                if (isListNotEmpty(status)) {
                    queryConstraints.push(where('status', 'in', status));
                } else if (isStringNotEmptyOrZeroLength(status)) {
                    queryConstraints.push(where('status', '==', status));
                }

                if (isNotNullOrUndefined(start)) {
                    let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
                    queryConstraints.push(where('nextPaymentDate', '>=', startDate));
                }

                if (isNotNullOrUndefined(end)) {
                    let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
                    queryConstraints.push(where('nextPaymentDate', '<=', endDate));
                }

                let q = null;
                if (isObjectNotEmpty(next)) {
                    q = query(collection(db, Subscription.collectionName), ...queryConstraints, orderBy('nextPaymentDate', 'asc'), orderBy('__name__', 'asc'), limit(10000), startAfter(next.nextPaymentDate, next.id));
                } else {
                    q = query(collection(db, Subscription.collectionName), ...queryConstraints, orderBy('nextPaymentDate', 'asc'), orderBy('__name__', 'asc'), limit(10000));
                }

                let list = await getByQuery(q);

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }

    static getCountByNextDate = async (dateFieldName, programId, status, start, end) => {
        let queryConstraints = [];

        if (isStringNotEmpty(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmpty(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where(dateFieldName, '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where(dateFieldName, '<=', endDate));
        }

        let q = query(collection(db, Subscription.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        return snapShot.data().count;
    }

    static getByNextDate = async (dateFieldName, programId, status, start, end) => {
        let allRecords = [];
        try {
            let next = null;
            let totalPages = 0;

            let totalCount = await Subscription.getCountByNextDate(dateFieldName, programId, status, start, end);
            totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let queryConstraints = [];

                if (isStringNotEmptyOrZeroLength(programId)) {
                    queryConstraints.push(where('programId', '==', programId));
                }

                if (isListNotEmpty(status)) {
                    queryConstraints.push(where('status', 'in', status));
                } else if (isStringNotEmptyOrZeroLength(status)) {
                    queryConstraints.push(where('status', '==', status));
                }

                if (isNotNullOrUndefined(start)) {
                    let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
                    queryConstraints.push(where(dateFieldName, '>=', startDate));
                }

                if (isNotNullOrUndefined(end)) {
                    let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
                    queryConstraints.push(where(dateFieldName, '<=', endDate));
                }

                let q = null;
                if (isObjectNotEmpty(next)) {
                    q = query(collection(db, Subscription.collectionName), ...queryConstraints, orderBy(dateFieldName, 'asc'), orderBy('__name__', 'asc'), limit(10000), startAfter(next[dateFieldName], next.id));
                } else {
                    q = query(collection(db, Subscription.collectionName), ...queryConstraints, orderBy(dateFieldName, 'asc'), orderBy('__name__', 'asc'), limit(10000));
                }

                let list = await getByQuery(q);

                //  console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                //  console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }

    static updateSubscriptions = async () => {
        const list = await Subscription.get({ start: '01/01/2023', end: '12/03/2024' });
        const ordersList = await Order.get({ start: '01/01/2023', end: '12/03/2024' });
        // console.log(list.length);
        // console.log(ordersList.length);

        const productMatches = await Product.getByField('sku', 'COVID-19', '==');
        const product = new Product(productMatches[0]);

        let chunks = [];
        for (let i = 0; i < list.length; i += 500) {
            chunks.push(list.slice(i, i + 500))
        }
        // console.log(chunks.length);
        for (var chunk of chunks) {
            // Get a new write batch
            var batch = writeBatch(db);
            chunk.map((item) => {
                const ref = doc(collection(db, Subscription.collectionName), item.id);

                let updatedFields = {};

                batch.update(ref, updatedFields);
            });
            await batch.commit();
            // console.log('done');
        }
    }

    static updateSubscriptions2 = async () => {
        let nextPage = null;
        let successCount = 0;
        let skipCount = 0;
        let totalPages = 0;
        let totalCount = await Subscription.getCount({}, 'createdAt');
        totalPages = Math.ceil(totalCount / 500.0);
        console.log(totalCount);
        console.log(totalPages);
        let pages = [];
        for (let i = 0; i < totalPages; i++) {
            pages.push(i + 1);
        }

        for (const page of pages) {
            console.log('starting page ' + page);
            const list = await Subscription.getPaginated({}, 500, null, nextPage, 'createdAt', 'createdAt');
            console.log('fetched subscriptions: ' + list.length);

            var batch = writeBatch(db);
            list.map((item) => {
                const subscriptionRef = doc(collection(db, Subscription.collectionName), item.id);

                if (isNullOrUndefined(item?.isStacked)) {
                    let updatedFields = {
                        isStacked: false,
                        stackedOptions: [],
                        stackedSelections: [],
                        stackEncounters: false,
                        stackShipments: false
                    };

                    batch.update(subscriptionRef, updatedFields);
                    successCount++;
                } else {
                    skipCount++;
                }
            });

            await batch.commit();

            console.log('go to next page', successCount, skipCount);
            nextPage = list[list.length - 1];
        }
    }
};

export default Subscription;
